// First of all: the game moved address (game/move.ts). On the old one nothing else starts; arriving, its data comes in.
import { moveState } from './game/moveFirst';
import '@fontsource/league-gothic/latin-400.css';
import '@fontsource/barlow-condensed/latin-500.css';
import '@fontsource/barlow-condensed/latin-600.css';
import '@fontsource/barlow-condensed/latin-700.css';
// Only the trackside sponsor boards still letter in these.
import '@fontsource/bodoni-moda/latin-700-italic.css';
import '@fontsource/bodoni-moda/latin-900-italic.css';
import '@fontsource/archivo-black/latin-400.css';
import './styles/main.css';

import { AudioEngine } from './audio/audio';
import { type EraId, ERAS, isEraId } from './config/eras';
import { CAMERA, eraPhysics, type PhysicsTuning, SIM_DT } from './config/tuning';
import { formatDate, formatPosterDate, isValidDate, msUntilRomeMidnight, romeDateString, shiftDate } from './game/daily';
import { hasPlayer, loadPlayer, madeUpName, newPlayerId, type Player, savePlayer } from './game/player';
import { deltaAt, type GhostTrack, type LapRecord, physicsHash, replayRecord } from './game/replay';
import { type GhostSource, Session } from './game/session';
import { beginnerPlan, brakingEvents, firstTurn, type LessonTurn, topPlan } from './game/lesson';
import { type LessonEntry, LessonDirector } from './game/lessonDirector';
import { RUN_UP, Spectator } from './game/spectator';
import { ownLapFor, sectorSplit } from './game/versus';
import { InputManager, type RawInput } from './input/input';
import { disableZoom } from './input/noZoom';
import { isTouchDevice, type Pedals, TouchControls } from './input/touch';
import { HttpApi } from './net/api';
import { isProfane } from './net/profanity';
import {
  type Battle,
  type Board,
  type BoardEntry,
  cleanName,
  type LinkNoLap,
  NO_REPLAY,
  type Podium,
  SHARE_ID,
  TOO_MANY_REFUSED,
  VIA_MAX,
  type Paper,
  type WatchLap,
} from './net/protocol';
import { canSend, copyText, newShareId, sendText, shareText, shareUrl, shortLap, topMedal } from './net/share';
import { arrivalSite, withoutFrom } from './net/arrival';
import type { StatKey } from './net/stats';
import { firstForLink, StatsReporter } from './net/statsReporter';
import { reportProblem, sendFeedback, sentWith, watchForErrors } from './net/report';
import type { CameraTarget } from './render/camera';
import type { CarVisual } from './render/car/carRig';
import { createRenderer, disposeWorld, drawsInSoftware, World } from './render/world';
import { type Moment, Sharpness } from './render/sharpness';
import { pressPhoto } from './render/pressPhoto';
import { clippingPicture } from './render/clipping';
import { figurePictures, podiumFigures } from './render/podiumArt';
import { MorningPaper } from './ui/paper';
import { type DiscordSession, IN_DISCORD, openOutside, shareChallenge, startDiscord } from './discord/activity';
import { BACK_FROM_DISCORD, beginSignIn, claim, forgetPlayer, leaveNote, readReturn, setSignedIn, signedInAs, type SignInReturn, takeNote } from './discord/web';
import { addedFrom, type BringServer, bringDue, bringSeen, bringShown, discordInstallUrl, PADDOCK_INVITE } from './net/discord';
import { LapPoster } from './discord/posts';
import { LiveLink } from './discord/live';
import { type LiveCar, type LiveNews, LiveWatch, RACING } from './game/liveWatch';
import { type LiveDrive, type LiveRoster, watchingYou } from './net/live';
import type { LiveLaunch } from './net/liveMessage';
import { issueOf, type PaperDays, paperShareText, paperWords } from './ui/paperWords';
import { quantizeInput } from './sim/input';
import { pointAt } from './sim/trackQuery';
import { Rescue } from './game/rescue';
import type { FilmHost } from './film';
import { botInput, type BotPlan, makePlan, trackPosition } from './track/bot';
import { spawnState } from './sim/world';
import { Surface } from './track/types';
import { generateCircuit, type GeneratedCircuit } from './track/generate';
import { generateCircuitName } from './track/names';
import { Rng } from './track/prng';
import { fetchPrebuilt } from './track/prebuilt';
import { drawShareCard } from './render/shareCard';
import { bootDone, bootProblem } from './ui/boot';
import { loadFonts } from './render/textures';
import type { BoardScope, BoardStatus } from './ui/board';
import { DevPanel, type OverlayName } from './ui/devpanel';
import { isSettingId, SETTING_NAMES, SETTINGS_FROM, settingForDate } from './track/setting';
import { circuitDate } from './track/calendar';
import { DEV, DEV_HOST } from './dev';
import { carNote } from './game/carNotes';
import { formatLap, ordinal } from './ui/format';
import { Hud, RACE_NEXT, type RescueRow, WATCH_ONLY, type WatchWords } from './ui/hud';
import { LessonUi } from './ui/lessonUi';
import { MiniMap } from './ui/minimap';
import { gameSize, watchGameSize } from './ui/fullscreen';
import { posterStyleFor } from './ui/posterStyle';
import { type ChallengeIntro, circuitTag, type LessonTicket, type PodiumInfo, type PollAnswer, type PosterAction, Screens, type TitleInfo, type TodayInfo } from './ui/screens';
import { trackOutlineSvg } from './ui/trackOutline';

type Mode = 'loading' | 'title' | 'running' | 'paused' | 'watching';

/** Someone's lap to watch, then race as a ghost: a friend's link, a board row, yesterday's winner. */
interface Challenge {
  kind: 'friend' | 'winner';
  /** '' for a lap shared without a name. */
  name: string;
  lapMs: number;
  record: LapRecord;
  rank: number | null;
  total: number | null;
  /** Watched to the end (or skipped): the poster says "your turn". */
  watched: boolean;
  /** Your best clean lap beat it by this much (ms), or null. */
  beatBy: number | null;
  /** A friend's link opened after its day: that race is over, the lap is only to watch. */
  expired: boolean;
  /** The steps already counted for the stats (opened, watched, raced, beaten, sent back: once each). */
  counted: Set<StatKey>;
  /** The link it came by (a friend's), and whether it's the player's own lap (their link, opened to check it). */
  linkId?: string;
  own?: boolean;
  /** Taken from a battle's news ("take it back"): a rival's lap, not a link's. */
  battle?: boolean;
  /** Yesterday's winner's lap: their car (the ghost raced) is gold today. */
  champion?: boolean;
}

/** A friend's link whose driver has no lap today. */
const isNoLap = (x: WatchLap | LinkNoLap): x is LinkNoLap => (x as LinkNoLap).none === true;

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/** How to name a past day: "yesterday", "monday" (this last week), or "20 september". */
function pastDay(date: string, today: string): { when: string; whose: string } {
  const days = Math.round((Date.parse(today + 'T12:00:00Z') - Date.parse(date + 'T12:00:00Z')) / 86400000);
  if (days <= 1) return { when: 'yesterday', whose: 'yesterday’s' };
  const d = new Date(date + 'T12:00:00Z');
  if (days < 7) return { when: `on ${WEEKDAYS[d.getUTCDay()]}`, whose: `${WEEKDAYS[d.getUTCDay()]}’s` };
  return { when: `on ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`, whose: `the ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}` };
}

const params = new URLSearchParams(location.search);
/** Where this visit came from (net/arrival.ts): read before anything changes the address. */
const ARRIVAL = arrivalSite(location.search, document.referrer, IN_DISCORD);
/** ?setting=liguria (or dolomites, sicily, tuscany): the world in that setting, to try them (look.ts). */
const SETTING_PARAM = ((v) => (isSettingId(v) ? v : null))(params.get('setting'));

/** A computer that dropped frames even at the lowest sharpness gets the light scene, remembered a week (as sharpness is). */
const LIGHT_KEY = 'gp.light';
const LIGHT_DAYS = 7;
/** Phones: the gas and brake as buttons or a slider (the pause poster's switch), and the slider's how-to, shown on
 *  its first races. */
const PEDALS_KEY = 'gp.pedals';
const DEMO_KEY = 'gp.slideDemo';
const DEMO_RACES = 2;
/** The pause poster's "screen shake" switch: 'off' stills the camera (no speed shake, grass bob or wall jolt). */
const SHAKE_KEY = 'gp.shake';
/** On the website: the day the "race together on Discord" card was shown (once per browser). */
const DISCORD_CARD_KEY = 'gp.discordCard';
/** The paper's line to il paddock (the Grand Premio server on Discord): '1' once tapped, and then it's left out. */
const PADDOCK_KEY = 'gp.paddock';
/** Inside Discord: when the "bring it to your server" card last came up, and how often for each server. */
const BRING_KEY = 'gp.bring';
/** Friends driving now on your track (in a Discord server): 'off' when switched off in the poster's "on track now" strip. */
const FRIENDS_KEY = 'gp.friends';
/** The server's channel shows you while you drive (net/liveMessage.ts): 'off' when switched off under the leaderboard. */
const LIVE_SHOWN_KEY = 'gp.discordLive';
/** Opened from "Watch" or "Race with" on the channel's message: the room has this long to say who's on track. */
const LAUNCH_WAIT_MS = 12_000;

/** ?sharp=1.5: draw the 3D view at a fixed sharpness (pixels per point), for testing. */
const SHARP_PARAM = ((): number | null => {
  const v = Number(params.get('sharp'));
  return v >= 0.5 && v <= 3 ? v : null;
})();

/** ?timing=1: how long each step of starting up takes, in the console. */
const TIMING = params.has('timing');
let lastMark = 0;
function mark(step: string): void {
  if (!TIMING) return;
  const now = performance.now();
  console.log(`[timing] ${step}: +${Math.round(now - lastMark)} ms (at ${Math.round(now)} ms)`);
  lastMark = now;
}

// The test copy of the game (gran-premio-staging…) says so in a corner, so it's never mistaken for the live one.
if (location.hostname.includes('staging')) document.body.classList.add('staging');

// A hello for whoever opens the browser's tools on the game (a cheat sending a made-up lap by hand did, 14 October
// 2026): friendly, since honest players see it too.
console.log(
  '%cGRAND PREMIO%c\nLooking under the hood? 🔧\nFast laps are driven again on our server, from their own inputs, so an edited time comes straight off the board. The quickest way up is still the throttle.\nFound a real bug? hello@grandprem.io',
  'font: 34px "League Gothic", Oswald, sans-serif; color: #b8352b; letter-spacing: 0.04em',
  'font: 13px system-ui, sans-serif; line-height: 1.5',
);

/**
 * ?lesson=reset makes this browser a first-timer again for the driving lesson (the offer comes back on the
 * next race); ?lesson=1 offers it on the next race without forgetting anything.
 */
const LESSON_PARAM = params.get('lesson');
if (LESSON_PARAM === 'reset') {
  try {
    localStorage.removeItem('gp.lesson');
    localStorage.removeItem('gp.laps');
  } catch {
    // No storage: every visit is a first visit anyway.
  }
}

/** A number or word this browser keeps (the lap count, the poll's answer); null without storage. */
function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // No storage (private mode): forgotten next visit.
  }
}

function removeLocal(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // No storage: nothing to forget.
  }
}

/**
 * Whether this browser has ever won a medal: remembered once won, and for players from before that was
 * remembered, read from the days they kept (the lesson is never suggested to medal winners).
 */
function everWonMedal(): boolean {
  if (readLocal('gp.medal') === '1') return true;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('granpremio:v2:day:')) continue;
      const m = (JSON.parse(localStorage.getItem(k) ?? 'null') as { medals?: Record<string, boolean> } | null)?.medals;
      if (m && (m.bronze || m.silver || m.gold)) {
        writeLocal('gp.medal', '1');
        return true;
      }
    }
  } catch {
    // Unreadable storage: as if none.
  }
  return false;
}

/** The poll's answers, as the stats count them. */
const POLL_STAT: Record<PollAnswer, StatKey> = {
  up: 'pollUp',
  down: 'pollDown',
  skip: 'pollSkip',
  hard: 'whyHard',
  controls: 'whyControls',
  slow: 'whySlow',
  samey: 'whySamey',
};

/** The poll comes after this many finished laps (on this browser, ever). */
const POLL_AFTER_LAPS = 5;

class Game {
  private canvas = document.getElementById('scene') as HTMLCanvasElement;
  private ui = document.getElementById('ui') as HTMLElement;
  /** How sharp the 3D view is drawn, adapted to the device (never during a lap). ?sharp=1.5 fixes it. */
  private sharp = new Sharpness(window.devicePixelRatio || 1, SHARP_PARAM);
  private renderer = createRenderer(this.canvas, this.sharp.ratio);
  /** This visit's sharpness has been counted for the stats (at its first race), and a struggling device. */
  private sharpCounted = false;
  private slowCounted = false;
  /**
   * The light scene (fewer trackside things, smaller shadows, simpler ground): phones always, a computer that
   * dropped frames even at the lowest sharpness (for a week), or ?light=1 to try it.
   */
  private light = isTouchDevice() || params.get('light') === '1' || Date.now() - Number(readLocal(LIGHT_KEY) ?? 0) < LIGHT_DAYS * 86400000;
  private lightCounted = false;
  private api = new HttpApi();
  /** Asked before loadPlayer makes a player: whether this browser has opened the game before. */
  private playedBefore = hasPlayer();
  private player: Player = loadPlayer();
  /** Inside Discord (an Activity), signed in through it: the player, their server, the SDK. Null on the web. */
  private discord: DiscordSession | null = null;
  /** Inside a Discord server: the day's best lap, posted in its channel when you leave. */
  private lapPoster: LapPoster | null = null;
  /** Inside a Discord server: its room, to watch the players driving right now and be watched (discord/live.ts). */
  private live: LiveLink | null = null;
  /** Watching friends drive right now (game/liveWatch.ts), while it's on. */
  private liveWatch: LiveWatch | null = null;
  /** This game's run in the room: a new number at each restart (from a random start, so a reload is a new run). */
  private liveRun = Math.floor(Math.random() * 1e9);
  /** Watching live began from a paused race (back to it after). */
  private liveFromPause = false;
  /** The other drivers' keys, in their chips' order (to follow another). */
  private livePeers: string[] = [];
  /** The one followed stopped driving: on to the next one (or the poster) in a moment. */
  private liveGoneTimer = 0;
  /** The server's channel may show you while you drive (the switch under the leaderboard). */
  private liveShown = readLocal(LIVE_SHOWN_KEY) !== 'off';
  /** Opened from the channel's "on track now": whom to watch or race with, once the room and the poster are up. */
  private liveLaunch: (LiveLaunch & { at: number }) | null = null;
  /** Racing with this driver, from "Race with" on the channel's message: their car shows even with the strip's switch off. */
  private raceWith: { key: string; name: string } | null = null;
  /** On the website: this copy of the game's Discord app (its id, for the add links), or null: none to add yet. */
  private discordApp: string | null = null;
  /** Discord's sign-in page (the website's "sign in with Discord"), from the server's settings. */
  private discordAuthorize: string | null = null;
  /** Where Discord's add screen comes back to once the manager has chosen the posts' channel (null: none asked for). */
  private discordAdded: string | null = null;
  /** What the poster says once it's up, after coming back from Discord's sign-in. */
  private signInNote: string | null = null;
  /** Back from a sign-in this browser didn't begin: asked "sign in as …?" once the poster's up. */
  private signInAsk: Extract<SignInReturn, { kind: 'ticket' }> | null = null;
  private discordCardTimer = 0;
  /** Inside Discord: a lap went on the board this visit (the "bring it to your server" card may follow), and the card came up already. */
  private savedThisVisit = false;
  private bringDone = false;
  private bringTimer = 0;
  /** Racing in a Discord server: the others driving now, their cars on your track as you lap (game/liveWatch.ts). */
  private together: LiveWatch | null = null;
  /** Name flags are up over friends' cars (cleared once, when there are none). */
  private togetherFlags = false;
  /** Friends' cars shown on your track (the "on track now" strip's switch; they still see yours either way). */
  private friendsOn = readLocal(FRIENDS_KEY) !== 'off';
  /** Who's on track now, as the poster's strip shows them. */
  private liveList: { key: string; name: string; status: string; paused: boolean }[] = [];
  /** The run in which friends' cars were last counted as on your track (stats: once a run). */
  private togetherCounted = -1;
  /** Inside a server: its own board, or everyone's (the switch on the poster and the leaderboard). */
  private boardScope: 'server' | 'everyone' = 'server';
  /** The game's own analytics: what happens while playing, tallied and sent every couple of minutes (net/stats.ts). */
  private stats = new StatsReporter(
    () => ({
      playerId: this.player.id,
      device: isTouchDevice() ? 'phone' : 'computer',
      input: this.controls(),
      today: romeDateString(),
      named: !!this.player.name,
      news: this.ownLinkId() !== '' || this.viaFor(romeDateString()) !== undefined,
      discord: IN_DISCORD,
    }),
    this.playedBefore,
  );
  /** Laps finished on this browser, ever: the poll waits for the fifth. */
  private lifetimeLaps = Number(readLocal('gp.laps')) || 0;
  /** A rebuilt car's note is up (tellCarNote): the car waits for "got it". */
  private noteHold = false;
  /** The driving lesson in progress, or null. */
  private lesson: LessonDirector | null = null;
  private lessonUi: LessonUi;
  /** The lesson's gentler plan for today's car, its braking events round the lap, and turn 1 (the first real stop after the start). */
  private lessonPlan!: BotPlan;
  /** The lesson's green-zone top: quicker than the beginner plan, the most that's still fine. */
  private lessonTop!: BotPlan;
  private lessonEvents: LessonTurn[] = [];
  private turnOne!: LessonTurn;
  private turnOneIndex = -1;
  private spawnIndex = 0;
  /** The next race from the poster starts the lesson (asked for from the poster). */
  private lessonAsked: LessonEntry | null = null;
  /** ?lesson=1: offer the lesson on the next race whatever this browser remembers. */
  private lessonForced = LESSON_PARAM === '1' || LESSON_PARAM === 'reset';
  /** The lesson has been offered this visit. */
  private lessonOffered = false;
  /** Restarts since the last finished lap, this visit: after a few, the poster suggests the lesson. */
  private restartsWithoutLap = 0;
  /** This start has got past turn 1 (counted once per start, for the stats). */
  private pastTurnOne = false;
  /** The controls as last read while racing. */
  private lastRaw: RawInput = { throttle: 0, brake: 0, steer: 0 };
  /** Stuck or crashed: how to back out, or the restart, taught until a player knows (game/rescue.ts). */
  private rescue = new Rescue({ read: readLocal, write: writeLocal }, (n) => this.stats.count(n));
  /** Today's leaderboard as last fetched, and the card's state. */
  private board: Board | null = null;
  private boardStatus: BoardStatus = 'loading';
  private boardBusy = false;
  private boardEditing = false;
  private boardMessage: string | null = null;
  private boardFetchedAt = -Infinity;
  /** The next target's lap handle as last seen (undefined before the first board of the day), to tell when a new one unlocks. */
  private lastTarget: string | null | undefined = undefined;
  private challenge: Challenge | null = null;
  /** The replay on screen while watching. */
  private spectator: Spectator | null = null;
  private watchBannerShown = false;
  /** Yesterday's podium, for today's poster. */
  private podium: Podium | null = null;
  /** Yesterday's podium, asked for the moment the page starts (it only needs the date). */
  private earlyPodium: Promise<Podium | null> | null = null;
  /** The morning paper: yesterday's race for this player (null: nothing to tell), asked for as the page starts. */
  private paper: Paper | null = null;
  private earlyPaper: Promise<Paper | null> | null = null;
  private morning!: MorningPaper;
  /** The paper's press photo and its share picture, drawn while it's open (so sharing is instant). */
  private paperPhoto: Promise<string> | null = null;
  private paperClip: File | null = null;
  /**
   * Laps watched together (lined up to cross the line at the same moment), one followed by the camera
   * (`focus`), the others as ghosts: yesterday's podium, or a lap with your own best beside it (`own`).
   */
  private group: {
    podium: boolean;
    /** `gold`: yesterday's winner's lap, today (their car is gold). */
    laps: { place: number; name: string; lapMs: number; record: LapRecord; own?: boolean; gold?: boolean }[];
    focus: number;
    runners: Spectator[];
    /** Each lap's time at every metre (for the gaps between them). */
    tracks: (GhostTrack | null)[];
    /** The words the replay began with (when following your lap, they speak of theirs beside it). */
    words?: WatchWords;
  } | null = null;
  /** Your lap on today's board (fetched when this device has none as good), for replays. */
  private boardOwn: { ref: string; record: LapRecord | null } | null = null;
  /** Your ghost in replays, hidden with its chip (remembered). */
  private ownGhostOff = readLocal('gp.ownGhost') === 'off';
  /** A short note on the poster ("that link leads nowhere"). */
  private notice: string | null = null;
  /** After an old challenge link: who to challenge back on today's circuit ('' for an unnamed one), until you share a lap. */
  private challengeBack: string | null = null;
  /** A controller has been used: hints speak its buttons. */
  private padSeen = false;
  /**
   * A lap from the leaderboard being watched: just to look at, not a challenge
   * (no ghost, no "your turn", no "send it back"; any challenge stays as it was).
   * `resume`: you'd paused mid-lap, and go back to it afterwards.
   */
  private boardView: { name: string; own: boolean; rank: number; lapMs: number; record: LapRecord; resume: boolean; champion?: boolean } | null = null;
  /** Your best lap's link, once made: the same lap keeps the same link. */
  private shared: { day: string; id: string; sent: string | null } | null = null;
  /** Battles' news already given as a banner this visit (their laps' handles). */
  private toldLive = new Set<string>();
  /** A new personal best this visit: a good moment for the share nudge. */
  private goodLap = false;
  /** Sharing asked for a name first: once it's saved, say so and point back at the share links. */
  private shareAfterName = false;
  /** Link-preview pictures (JPEG, base64) by share key, drawn ahead while the poster is up. */
  private cards = new Map<string, Promise<string | null>>();
  private audio = new AudioEngine();
  private input = new InputManager();
  private touch: TouchControls;
  private hud: Hud;
  private screens: Screens;
  private dev: DevPanel;
  private minimap: MiniMap | null = null;
  private circuit!: GeneratedCircuit;
  private session!: Session;
  private world!: World;
  private plan!: BotPlan;
  private mode: Mode = 'loading';
  private acc = 0;
  private last = performance.now();
  private fps = 60;
  /** The bot drives (?autopilot=1: only on the maker's own copies, like the dev panel; screenshot runs and filming too). */
  private autopilot = DEV_HOST && params.get('autopilot') === '1';
  /** Filming the trailer (src/film.ts): no ghost car, unless a shot wants one. */
  private filming = false;
  private filmGhosts = false;
  /** Dev preview: drive another era's car on today's seed (?era=1972 or the dev panel). */
  private eraOverride: EraId | null = isEraId(params.get('era')) ? (params.get('era') as EraId) : null;
  private shotAt = params.has('shot') ? Number(params.get('shot')) : -1;
  private excitement = 0;
  private ghostOut = new Array(9).fill(0);
  private simTime = 0;
  private overlays: Record<OverlayName, boolean> = { sectors: false, curvature: false, braking: false, racingLine: false };

  constructor() {
    this.hud = new Hud(this.ui);
    this.touch = new TouchControls(this.ui, (a) => this.input.fire(a), () => (this.input.lastDevice = 'touch'));
    this.touch.setPedals(readLocal(PEDALS_KEY) === 'slider' ? 'slider' : 'buttons');
    this.input.addSource(this.touch);
    this.screens = new Screens(this.ui);
    this.morning = new MorningPaper(this.ui);
    this.lessonUi = new LessonUi(this.ui);
    this.lessonUi.onTake = () => this.lesson?.take();
    this.lessonUi.onSkip = () => this.lesson?.skip();
    this.lessonUi.onLeave = () => this.lesson?.leave();
    this.lessonUi.onContinue = () => this.lesson?.proceed();
    this.lessonUi.onRaceForReal = () => this.lesson?.raceForReal();
    this.lessonUi.onHowToPlay = () => this.lesson?.howToPlay();
    this.lessonUi.onSkipDemo = () => this.lesson?.skipDemo();
    this.screens.onLesson = () => this.askLesson('poster');
    this.screens.onLine = () => {
      writeLocal('gp.line', this.lineOn() ? 'off' : 'on');
      this.showLine();
      this.updatePoster();
    };
    this.screens.onGo = () => this.go();
    this.screens.onPedals = () => {
      const next: Pedals = this.touch.pedalKind === 'slider' ? 'buttons' : 'slider';
      this.touch.setPedals(next);
      writeLocal(PEDALS_KEY, next);
      this.stats.count(next === 'slider' ? 'sliderOn' : 'sliderOff');
      this.updatePoster();
    };
    this.screens.onFriends = () => {
      this.friendsOn = !this.friendsOn;
      writeLocal(FRIENDS_KEY, this.friendsOn ? 'on' : 'off');
      this.stats.count(this.friendsOn ? 'friendsOn' : 'friendsOff');
      this.screens.setLive(this.liveList, this.friendsOn);
    };
    this.screens.onShake = () => {
      const on = !this.shakeOn();
      writeLocal(SHAKE_KEY, on ? 'on' : 'off');
      this.world.chase.motion = on;
      this.stats.count(on ? 'shakeOn' : 'shakeOff');
      this.updatePoster();
    };
    this.screens.onAction = (id) => {
      if (id === 'race') this.go();
      else if (id === 'today') this.leaveForToday();
      else if (id === 'watch') this.watch();
      else if (id === 'sendBack') this.shareLap('send');
    };
    this.screens.onChallenge = (from) => this.challengeNow(from);
    this.screens.board.onChallenge = () => this.challengeNow('board');
    this.screens.onWatchPodium = (place) => void this.watchPodium(place);
    this.screens.onScope = (on) => this.setBoardScope(on);
    this.screens.board.onScope = (on) => this.setBoardScope(on);
    this.screens.board.onPosts = (on) => {
      this.lapPoster?.setEnabled(on);
      this.renderBoard();
    };
    this.screens.board.onLiveShown = (on) => {
      this.liveShown = on;
      writeLocal(LIVE_SHOWN_KEY, on ? 'on' : 'off');
      this.live?.announce(on);
      this.stats.count(on ? 'liveShownOn' : 'liveShownOff');
      this.renderBoard();
    };
    this.screens.onPaper = () => {
      this.stats.count('paperReread');
      this.showPaper();
    };
    this.screens.onWatchEntry = (e) => void this.watchBoardLap(e);
    this.screens.onWatchLive = (key) => this.watchLive(key);
    this.screens.onSaveLap = () => {
      this.stats.count('boardViews');
      this.screens.openBoard();
      this.screens.board.focusName();
    };
    this.hud.onPeer = (i) => this.followLap(i);
    this.hud.onOwnGhost = () => this.toggleOwnGhost();
    this.hud.onDropRival = () => this.dropChallenge();
    this.screens.onDropGhost = () => this.dropChallenge();
    this.screens.board.onSignIn = () => this.signInWithDiscord();
    this.screens.onInvite = () => this.inviteFriends();
    this.stats.onNews = (battles) => this.battleNews(battles);
    this.screens.onOpened = (view) => {
      if (view === 'board') this.stats.count('boardViews');
    };
    this.screens.onDoor = () => {
      this.stats.count('discordDoor');
      this.showDiscordPop(false);
    };
    this.screens.onPoll = (a) => this.answerPoll(a);
    this.screens.onFeedback = (text) => sendFeedback(this.player.id, text, this.mode === 'paused' ? 'paused' : 'poster');
    this.screens.setFeedbackContext(sentWith());
    // A step down in sharpness (only ever on the poster or the pause menu): redraw at the new size.
    this.sharp.onChange = (ratio) => {
      this.renderer.setPixelRatio(ratio);
      this.resize();
    };
    // A browser drawing without the graphics card: a line on the poster says how to turn it on.
    if (drawsInSoftware(this.renderer)) {
      this.stats.count('gpuSoftware');
      this.screens.showGpuHint(() => this.gpuHelp());
    }
    // The beta's crash reports: what goes wrong on players' devices, and the 3D being lost (a black screen).
    watchForErrors(() => this.mode);
    this.canvas.addEventListener('webglcontextlost', () => reportProblem('gl-lost', 'WebGL context lost', this.mode));
    this.screens.onToday = () => this.goToday();
    this.screens.board.onWatch = (e) => void this.watchBoardLap(e);
    this.hud.onSkip = () => this.endWatch();
    this.screens.onSound = () => {
      this.audio.setMuted(!this.audio.isMuted);
      this.screens.setSound(this.audio.isMuted);
    };
    this.screens.setSound(this.audio.isMuted);
    this.screens.board.onSave = (name) => void this.saveToBoard(name);
    this.screens.board.onEdit = () => {
      this.boardEditing = true;
      this.renderBoard();
      this.screens.board.focusName();
    };
    this.hud.onSaveChip = () => {
      if (this.mode === 'running') this.pause();
      this.stats.count('boardViews');
      // A made-up name on the board: the name form, that name selected to type over.
      if (this.madeUpName) {
        this.screens.closeAskName();
        this.boardEditing = true;
        this.renderBoard();
      }
      this.screens.openBoard();
      this.screens.board.focusName();
    };
    this.dev = new DevPanel(this.ui, {
      circuit: () => this.circuit,
      session: () => this.session,
      loadDate: (d, salt) => void this.load(d || romeDateString(), salt),
      eraOverride: () => this.eraOverride,
      setEra: (era) => {
        this.eraOverride = era;
        void this.load(this.circuit.date, this.circuit.salt);
      },
      setOverlay: (n, on) => {
        this.overlays[n] = on;
        this.world.dev[n].visible = on;
      },
      setShowFps: (on) => (this.hud.fpsEl.style.display = on ? '' : 'none'),
      setGhostSource: (src: GhostSource) => this.session.setGhostSource(src),
      setAutopilot: (on) => (this.autopilot = on),
      setPost: (on) => (this.world.postEnabled = on),
      setHud: (on) => this.hud.setVisible(on),
      rendererInfo: () => ({ calls: this.renderer.info.render.calls, triangles: this.renderer.info.render.triangles, fps: this.fps }),
      onPhysicsChanged: () => {
        this.plan = makePlan(this.circuit.line, this.session.physics, this.circuit.bot.gripLevel);
      },
      currentPhysics: () => eraPhysics(this.circuit.era),
      replayBest: () => {
        const best = this.session.day.best;
        if (!best) return null;
        const r = replayRecord(this.circuit.runtime, this.circuit.physics, best, false);
        const ok = r.completed && r.lapMs === best.lapMs && r.endTick === best.endTick;
        return { ok, detail: `stored ${formatLap(best.lapMs)} → replayed ${r.completed ? formatLap(r.lapMs) : 'incomplete'}${ok ? ', identical' : ''}` };
      },
    });

    this.input.onAction((a) => {
      if (a === 'devPanel') {
        if (DEV) this.dev.toggle();
        return;
      }
      if (a === 'challenge') {
        // C on the poster (or the pause menu): send your lap as a challenge; racing, the banner's "send it back".
        if (this.mode === 'title' || this.mode === 'paused') this.challengeNow('poster');
        else if (this.mode === 'running') this.hud.pressBannerAction();
        return;
      }
      if (a === 'mute') {
        this.audio.setMuted(!this.audio.isMuted);
        this.screens.setSound(this.audio.isMuted);
        this.hud.showBanner(this.audio.isMuted ? 'Muted' : 'Sound on', '', '', 1);
        return;
      }
      if (this.mode === 'watching') {
        // Esc, R, B or Start skips the replay (driving keys and pedals don't, so nobody skips by accident).
        if (a === 'pause' || a === 'restart' || a === 'back') this.endWatch();
        return;
      }
      // The morning paper: Enter, Space, Esc (or A, B) put it down; nothing starts a race under it.
      if (this.morning.open) {
        if (a === 'confirm' || a === 'pause' || a === 'back') this.morning.close();
        return;
      }
      if (this.screens.showingPopup) {
        if (a === 'confirm') this.screens.pressPopup(true);
        else if (a === 'pause' || a === 'back') this.screens.pressPopup(false);
        return;
      }
      // The name card over the pause menu: Esc (or B) keeps the made-up name; nothing else starts the race under it.
      if (this.screens.askingName) {
        if (a === 'pause' || a === 'back') this.keepMadeUpName();
        return;
      }
      if (this.mode === 'title' || this.mode === 'paused') {
        if (a === 'confirm') this.primaryAction();
        else if (a === 'back') this.screens.show('main');
        else if (a === 'restart') this.lessonFromPoster();
        else this.go();
        return;
      }
      if (this.mode !== 'running') return;
      // The lesson has its own meanings: Esc, controller B and Start leave it; R retries the try.
      if (this.lesson) {
        this.lesson.onAction(a);
        return;
      }
      if (this.noteHold) {
        // A controller's A (Enter on a keyboard: tellCarNote).
        if (a === 'confirm') this.hud.closeNote();
        else if (a === 'pause') this.pause();
        return;
      }
      if (a === 'restart') {
        this.rescue.restartPressed();
        this.restart();
        this.countRace();
        this.restartsWithoutLap++;
      }
      if (a === 'pause') this.pause();
    });

    if (isTouchDevice()) this.touch.show(true);
    window.addEventListener('touchstart', () => this.touch.show(true), { once: true, passive: true });
    window.addEventListener('resize', () => this.resize());
    // The size may settle without a resize (an iPhone home-screen app opening or coming back): check again
    // then, and redraw only if it changed.
    watchGameSize(() => {
      const v = gameSize();
      if (v.w !== this.view.w || v.h !== this.view.h) this.resize();
    });
    // A controller: its buttons on the poster, and its words in the hints (browsers announce one at its first press).
    window.addEventListener('gamepadconnected', () => {
      this.padSeen = true;
      this.hud.setHint(this.controlsHint());
      this.updatePoster();
    });
    document.addEventListener('visibilitychange', () => {
      // Frames around a tab switch say nothing about the device.
      this.sharp.quiet(performance.now());
      // Put away: a new best still being checked is finished (and saved) now, in case the page is closed.
      if (document.hidden) this.session?.verifyStep(Infinity);
      if (document.hidden && this.mode === 'running' && this.shotAt < 0) {
        if (this.lesson) this.lesson.hold();
        else this.pause();
      }
    });
    disableZoom();
    (window as unknown as { __gp: unknown }).__gp = this;
  }

  async start(): Promise<void> {
    mark('code running');
    this.screens.loading();
    // Back from Discord's sign-in (the website): the player is the Discord account's from here on.
    if (!IN_DISCORD && BACK_FROM_DISCORD) await this.backFromDiscord();
    // Inside Discord: signed in through it first (the player, the server), before anything asks with the player's id.
    if (IN_DISCORD) await this.signInDiscord();
    else void this.findDiscordApp();
    this.screens.setLegal(!IN_DISCORD);
    // A shared lap's link (?lap=…, or inside Discord a challenge it was opened from): fetch it while the fonts
    // load, then open its day.
    const lapId = params.get('lap') ?? this.discord?.linkId ?? null;
    const shared = lapId && SHARE_ID.test(lapId) ? this.api.getShare(lapId) : Promise.resolve(lapId ? ('missing' as const) : null);
    // Today's circuit, built ahead of time: fetched alongside the fonts (see track/prebuilt.ts).
    const today = romeDateString();
    const early = !lapId && !params.has('date') ? fetchPrebuilt(today) : undefined;
    // Yesterday's podium too, so it's on today's poster from the start.
    if (!lapId && !params.has('date') && !this.eraOverride) {
      this.earlyPodium = this.api.getPodium(shiftDate(today, -1));
      this.earlyPaper = this.api.getPaper(this.player.id);
    }
    await loadFonts();
    mark('fonts');
    const lap = await shared;
    const d = params.get('date');
    const date = lap && lap !== 'missing' && !isNoLap(lap) ? lap.record.date : d && isValidDate(d) ? d : today;
    await this.load(date, Number(params.get('salt') ?? 0) || 0, date === today ? early : undefined);
    if (lap === 'missing') {
      this.setNotice('That link has expired (links last two days) · here’s today’s circuit');
      // Counted once a person (opening it again adds nothing), and a link brought them all the same.
      if (lapId && firstForLink(lapId, 'linkGone')) this.stats.count('linkGone');
      this.stats.cameByLink(null);
    } else if (lapId && !lap) this.setNotice('Couldn’t fetch that lap · you’re offline');
    else if (lapId && lap) {
      if (isNoLap(lap)) this.openedNoLap(lapId, lap);
      else this.openedLink(lapId, lap);
    }
    this.afterSignIn();
    if (DEV && params.get('dev') === '1') this.dev.toggle();
    if (params.get('nohud') === '1') this.hud.setVisible(false);
    // A new player's first visit keeps where they came from; a posted link's ?from= goes from the address once read.
    this.stats.arrived(ARRIVAL);
    const unfrom = withoutFrom(location.href);
    if (unfrom !== null) history.replaceState(history.state, '', unfrom);
    // The visit counts from here, except screenshot runs and robots (crawlers and headless browsers name themselves).
    this.stats.begin(this.shotAt >= 0 ? 'a screenshot run' : /bot|crawl|spider|headless|lighthouse|preview/i.test(navigator.userAgent) ? 'a robot' : null);
    if (moveState === 'arrived') this.stats.count('movedIn');
    // Filming the trailer (?film=1, src/film.ts): a script drives the game frame by frame instead. Only on the
    // maker's own copies (like the dev panel): the bot drives in it, and its laps could reach a live board.
    if (params.get('film') === '1' && DEV_HOST) {
      this.autopilot = true;
      this.filming = true;
      const host = this.filmHost();
      void import('./film').then((m) => m.installFilm(host));
      return;
    }
    requestAnimationFrame((t) => this.frame(t));
  }

  /** The game's insides, for the filming mode (src/film.ts). */
  private filmHost(): FilmHost {
    return {
      go: () => this.go(),
      // Fractional steps carry over (the time-warp at each cut): what's left draws as the pose between ticks.
      step: (n) => this.tick(this.last + n * SIM_DT * 1000),
      rendered: () => this.session.state.tick - 1 + this.acc / SIM_DT,
      reset: () => {
        this.acc = 0;
      },
      ff: (n) => {
        const s = this.session;
        for (let i = 0; i < n; i++) {
          s.step(botInput(s.state.car, this.circuit.runtime, this.plan, s.physics));
          this.simTime += SIM_DT;
        }
      },
      world: () => this.world,
      hud: this.hud,
      car: () => {
        const s = this.session;
        const c = s.state.car;
        const t = s.state.timing;
        return { tick: s.state.tick, x: c.x, y: c.y, heading: c.heading, vx: c.vx, vy: c.vy, ax: c.ax, ay: c.ay, s: c.s, lapIndex: t.lapIndex, lapMs: s.lapElapsedMs(s.state.tick), started: t.started };
      },
      road: (s, h) => this.road(s, h),
      lateral: (s, x, y) => this.lateral(s, x, y),
      trackPoint: (s) => {
        const p = pointAt(this.circuit.runtime, s);
        return { x: p.x, y: p.y, h: this.road(s, 0).h };
      },
      // The self-driving car's skill (its grip level: above the car's own it overdrives and runs wide).
      setGrip: (g) => {
        this.plan = makePlan(this.circuit.line, this.session.physics, g);
      },
      restart: () => this.restart(),
      bestRecord: () => {
        this.session.verifyStep(Infinity);
        return this.session.day.best;
      },
      setGhosts: (on) => {
        this.filmGhosts = on;
      },
      audio: () => this.audio,
      engineSound: () => ERAS[this.circuit.era].sound,
      pause: () => this.pause(),
      mode: () => this.mode,
    };
  }

  private async load(date: string, salt: number, prebuilt?: Promise<GeneratedCircuit | null>): Promise<void> {
    this.mode = 'loading';
    this.screens.loading();
    await new Promise((r) => setTimeout(r, 30));
    // The day's circuit from its file when there is one (quick); otherwise built here, which takes a slow
    // phone seconds. Rerolls, previews and ?prebuilt=0 always build.
    const fromFile = salt === 0 && !this.eraOverride && params.get('prebuilt') !== '0' ? await (prebuilt ?? fetchPrebuilt(date)) : null;
    mark('circuit file');
    this.circuit = fromFile ?? generateCircuit(date, { salt, era: this.eraOverride ?? undefined });
    mark(fromFile ? 'circuit from its file' : 'circuit built here');
    this.audio.setEngine(ERAS[this.circuit.era].sound);
    // Staging and dev only: ?handling=next drives the day's circuit with the newest handling, to try a handling
    // pass on a familiar track before its day. Not the day's car, so its laps count as tuned (never saved).
    const physics = DEV_HOST && params.get('handling') === 'next' ? eraPhysics(this.circuit.era) : { ...this.circuit.physics };
    this.session = new Session(this.circuit, this.api, physics);
    const circuit = this.circuit;
    // The day's board (and on today's poster, yesterday's podium) are asked for now, while the world is
    // built, so the poster comes up with them already on it rather than having them pop in after.
    this.board = null;
    this.lastTarget = undefined;
    this.boardStatus = 'loading';
    this.boardBusy = false;
    this.boardEditing = false;
    this.boardMessage = null;
    const boardIn = this.refreshBoard();
    this.podium = null;
    const todays = date === romeDateString() && !this.eraOverride;
    const podiumIn = todays ? this.fetchPodium(date) : Promise.resolve();
    const paperIn = todays ? this.fetchPaper(date) : Promise.resolve();
    // A faster or slower bot (?botgrip=1.1): the maker's own copies only.
    const botGrip = DEV_HOST ? Number(params.get('botgrip')) : 0;
    this.plan = makePlan(this.circuit.line, physics, botGrip > 0 ? botGrip : this.circuit.bot.gripLevel);
    // The driving lesson's gentler plan for this car: where a newcomer should brake, and how slow to go.
    this.dropLesson();
    this.lessonPlan = beginnerPlan(this.circuit, physics);
    this.lessonTop = topPlan(this.circuit, physics);
    this.lessonEvents = brakingEvents(this.circuit.track, this.lessonPlan.profile);
    this.turnOne = firstTurn(this.circuit.track, this.lessonPlan.profile);
    this.turnOneIndex = this.lessonEvents.findIndex((e) => e.minIndex === this.turnOne.minIndex);
    this.spawnIndex = spawnState(this.circuit.runtime, physics).car.trackIndex;
    // The dev panel's "today's handling on past days" carries over to the day just loaded.
    if (this.dev.state.currentHandling) this.dev.applyHandling();
    const ghostParam = params.get('ghost');
    if (ghostParam === 'bot' || ghostParam === 'none' || ghostParam === 'best') this.dev.state.ghost = ghostParam;
    mark('session and plan');
    this.buildWorld();
    // While the loading poster is up, the sound device open (silent), so it doesn't happen in the tap that starts a race.
    this.audio.prepare();
    mark('3D world');
    this.session.setGhostSource(this.dev.state.ghost);
    if (!this.minimap) this.minimap = new MiniMap(this.ui, this.circuit.track);
    else this.minimap.setTrack(this.circuit.track);
    this.session.on((e) => this.onSession(e));
    this.dropLive();
    this.together = null;
    // Another day, another challenge (a new one is set after loading).
    this.challenge = null;
    this.boardView = null;
    this.hud.setRival(null);
    this.group = null;
    this.boardOwn = null;
    this.notice = null;
    this.hud.setCircuit(this.circuit.name, ...this.subLine());
    this.hud.setBest(this.session.day.best?.lapMs ?? null);
    this.hud.setMedals(this.circuit.medals, this.session.day.medals);
    this.hud.setSectors([null, null, null]);
    this.hud.setHint(this.controlsHint());
    this.resize();
    this.world.chase.snap();
    this.simTime = 0;
    this.acc = 0;
    this.dev.refresh();
    if (this.shotAt >= 0) {
      bootDone();
      this.screens.hide();
      this.hud.setTitleMode(false);
      document.body.classList.remove('title-mode');
      this.autopilot = true;
      this.mode = 'running';
    } else {
      // Today's poster waits a moment (no more than that) for the board and the podium asked for above.
      if (todays) await Promise.race([Promise.all([boardIn, podiumIn, paperIn]), new Promise((r) => setTimeout(r, 1200))]);
      if (this.circuit !== circuit) return;
      this.mode = 'title';
      this.showTitle(false);
    }
  }

  /** The line under the circuit's name while racing, and a shorter one for upright phones. */
  private subLine(): [string, string] {
    const ms = msUntilRomeMidnight();
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const isToday = this.circuit.date === romeDateString();
    const car = ERAS[this.circuit.era];
    const closes = `closes in ${h}h ${m}m`;
    return [`${formatDate(this.circuit.date)} · ${car.name} ${car.year}${isToday ? ` · ${closes}` : ''}`, isToday ? closes : `${car.name} ${car.year}`];
  }

  private showTitle(paused: boolean): void {
    // Back from a replay of the champion's lap: the cars as they are for you again.
    this.syncGold();
    // "Race with" lasts the race it began.
    if (!paused) this.raceWith = null;
    // The poster (start, pause) is a safe moment: a new best still being checked is finished now, so the
    // poster, the board and "challenge your friends" all have it.
    this.session.verifyStep(Infinity);
    if (!paused) this.adoptBoardOwn();
    this.audio.setCarSounds(false);
    this.showLine();
    this.renderBoard();
    this.drawBoard();
    const fresh = performance.now() - this.boardFetchedAt > 8000;
    if (fresh) void this.refreshBoard();
    this.hud.setTitleMode(true);
    document.body.classList.add('title-mode');
    this.screens.art.show(this.circuit.era, this.champion());
    this.screens.setGold(this.champion());
    this.screens.setStreak(this.streakDays());
    this.screens.showTitle(this.posterInfo(paused));
    this.prepareShareCard();
    // A friend's challenge not yet watched: spelled out first (unless it's beaten already: a faster lap of yours
    // today, on this browser; then the main side says so, with "send it back"). Otherwise, on a first visit, how
    // it works.
    const ch = this.challenge;
    if (ch && !ch.watched && !paused && ch.beatBy === null) this.screens.show('challenge');
    // Someone who skipped the lesson sees how to play once, on their first poster after it.
    else this.screens.show(!paused && !ch && readLocal('gp.lesson') === 'skipped' && !this.seenHow() ? 'how' : 'main');
    // The board's news from a moment ago (a battle): over the poster, now it's up. Then, if nothing else is,
    // the share nudge.
    // The morning paper comes first; the board's news and the nudge wait for the next poster.
    if (!paused && this.maybePaper()) return;
    if (!fresh) this.battleNews(this.board?.battles, true);
    this.maybeNudge();
    this.maybeDiscordCard();
    if (!paused) this.maybeBring();
    if (!paused) this.followLaunch();
  }

  /**
   * On the website: is there a Discord app to add this game to (this copy's: staging's, or the live one once it
   * exists)? Until there is, nothing on the poster mentions Discord.
   */
  private async findDiscordApp(): Promise<void> {
    try {
      const r = await fetch('api/discord/config');
      const c = r.ok ? ((await r.json()) as { clientId?: unknown; authorize?: unknown; added?: unknown }) : null;
      this.discordApp = typeof c?.clientId === 'string' && /^\d{5,25}$/.test(c.clientId) ? c.clientId : null;
      this.discordAuthorize = typeof c?.authorize === 'string' && /^https?:\/\//.test(c.authorize) ? c.authorize : null;
      this.discordAdded = addedFrom(c?.added);
    } catch {
      this.discordApp = null;
    }
    if (this.discordApp && (this.mode === 'title' || this.mode === 'paused')) {
      this.updatePoster();
      this.drawBoard();
      this.maybeDiscordCard();
    }
  }

  /** On the website, with a Discord app: signing in with it (who as, or null), for the poster's switch and the leaderboard. */
  private signInInfo(): { as: string | null } | null {
    if (IN_DISCORD || !this.discordApp || !this.discordAuthorize || this.eraOverride || this.shotAt >= 0) return null;
    return { as: signedInAs() };
  }

  /** "Sign in with Discord": off to Discord's page, back to /signin (backFromDiscord). */
  private signInWithDiscord(): void {
    if (this.discordApp && this.discordAuthorize) beginSignIn(this.discordApp, this.discordAuthorize);
  }

  /**
   * Back from Discord's sign-in page: signed in straight away when it began in this browser, else asked "sign in as
   * …?" once the poster's up (someone's link can't sign anyone in unasked). Cancelled or failed: said on the poster.
   */
  private async backFromDiscord(): Promise<void> {
    const r = await readReturn();
    if (r.kind === 'cancelled') this.signInNote = 'Discord sign-in cancelled';
    else if (r.kind === 'failed') this.signInNote = 'Couldn’t sign in with Discord · try again';
    else if (r.ours) this.signInNote = await this.becomeDiscordPlayer(r.ticket);
    else this.signInAsk = r;
  }

  /** This device becomes the Discord account's player (its own laps joined in): what to tell the player. */
  private async becomeDiscordPlayer(ticket: string): Promise<string> {
    const p = await claim(ticket, this.player.id);
    if (!p) return 'Couldn’t sign in with Discord · try again';
    this.player = { id: p.id, name: p.name };
    savePlayer(this.player);
    setSignedIn(p.name);
    this.stats.count('discordSignIn');
    return p.joined ? `Signed in as ${p.name} · this device’s laps are on your Discord player now` : `Signed in as ${p.name} · your laps go with you to every device`;
  }

  /** Once the poster's up: what the sign-in (or a sign-out before the reload) left to say, or "sign in as …?". */
  private afterSignIn(): void {
    const note = this.signInNote ?? takeNote();
    this.signInNote = null;
    if (note) this.setNotice(note);
    const ask = this.signInAsk;
    this.signInAsk = null;
    if (!ask) return;
    this.screens.askSignIn({
      name: ask.name,
      onYes: () =>
        void this.becomeDiscordPlayer(ask.ticket).then((said) => {
          leaveNote(said);
          location.replace(location.pathname);
        }),
      onNo: () => this.setNotice('Not signed in'),
    });
  }

  /** Signing out (the website): this browser starts again as a new guest; the laps stay the Discord player's. */
  private signOutOfDiscord(): void {
    this.stats.count('discordSignOut');
    forgetPlayer();
    savePlayer({ id: newPlayerId(), name: '' });
    leaveNote('Signed out · this device starts as a new player (sign in again for your laps)');
    location.replace(location.pathname);
  }

  /** On the website, for someone who has raced (never before a first lap), with a Discord app to add: racing together lives there. */
  private discordPromo(): boolean {
    return !IN_DISCORD && !this.discord && this.discordApp !== null && this.lifetimeLaps > 0 && !this.challenge && !this.eraOverride && this.shotAt < 0;
  }

  /**
   * "New: race together on Discord", once per browser: a moment after the poster comes up, if nothing else is over
   * it (the paper, a battle, the share nudge: then it waits for a later poster).
   */
  private maybeDiscordCard(): void {
    window.clearTimeout(this.discordCardTimer);
    if (!this.discordPromo() || readLocal(DISCORD_CARD_KEY) !== null) return;
    this.discordCardTimer = window.setTimeout(() => {
      const quiet = !this.screens.showingPopup && !this.morning.open && !this.screens.askingName && this.screens.currentView === 'main';
      if ((this.mode !== 'title' && this.mode !== 'paused') || !quiet || !this.discordPromo() || readLocal(DISCORD_CARD_KEY) !== null) return;
      writeLocal(DISCORD_CARD_KEY, romeDateString());
      this.stats.count('discordCard');
      this.showDiscordPop(true);
    }, 1500);
  }

  /** Grand Premio on Discord, as a big card over the poster: by itself once (`isNew`), or from the line over race. */
  private showDiscordPop(isNew: boolean): void {
    this.screens.showDiscordPop({
      isNew,
      onServer: () => this.addToDiscord('server'),
      onApps: () => this.addToDiscord('apps'),
      onClose: () => isNew && this.stats.count('discordCardLater'),
      onPaddock: () => this.joinPaddock('card'),
      signIn: this.signInInfo(),
      onSignIn: () => this.signInWithDiscord(),
      onSignOut: () => this.signOutOfDiscord(),
    });
  }

  /** Inside Discord, the server whose "bring it to your server" card is due, or null. */
  private bringDue(): BringServer | null {
    const s = this.discord;
    if (!s || !s.bring.length || !this.savedThisVisit || this.bringDone || this.challenge || this.eraOverride) return null;
    return bringDue(s.bring, bringSeen(readLocal(BRING_KEY)), Date.now());
  }

  /**
   * Inside Discord, now and then: a server the player runs hasn't got the game. Its card comes up on the poster a
   * moment after a lap saved this visit, if nothing else is over it; at most once a week, three times for any one
   * server, once a visit (this browser keeps count: gp.bring).
   */
  private maybeBring(): void {
    window.clearTimeout(this.bringTimer);
    if (!this.bringDue()) return;
    this.bringTimer = window.setTimeout(() => {
      const server = this.bringDue();
      const s = this.discord;
      const quiet = !this.screens.showingPopup && !this.morning.open && !this.screens.askingName && this.screens.currentView === 'main';
      if (!server || !s || this.mode !== 'title' || !quiet) return;
      this.bringDone = true;
      writeLocal(BRING_KEY, JSON.stringify(bringShown(bringSeen(readLocal(BRING_KEY)), server.id, Date.now())));
      this.stats.count('bringShown');
      this.screens.showBringPop({
        server,
        onAdd: () => {
          this.stats.count('bringAdd');
          openOutside(s, discordInstallUrl(s.clientId, 'server', server.id, s.added));
        },
        onLater: () => this.stats.count('bringLater'),
      });
    }, 1500);
  }

  /** Inside Discord: Discord's own invite window, to bring friends into the game. */
  private inviteFriends(): void {
    if (!this.discord) return;
    this.stats.count('discordInvite');
    void this.discord.sdk.commands.openInviteDialog().catch(() => undefined);
  }

  /** Discord's own screen to add the game: to a server, or to your own apps (a new tab, or the Discord app on a phone). */
  private addToDiscord(where: 'server' | 'apps'): void {
    if (!this.discordApp) return;
    this.stats.count(where === 'server' ? 'discordAddServer' : 'discordAddApps');
    window.open(discordInstallUrl(this.discordApp, where, undefined, this.discordAdded), '_blank', 'noopener');
  }

  /** Redraw the poster in place (something arrived: yesterday's winner, a note), keeping its side. */
  private updatePoster(): void {
    if (this.mode !== 'title' && this.mode !== 'paused') return;
    this.screens.art.show(this.circuit.era, this.champion());
    this.screens.setGold(this.champion());
    this.screens.setStreak(this.streakDays());
    this.screens.showTitle(this.posterInfo(this.mode === 'paused'));
    // The paper arrived after the poster went up.
    if (this.mode === 'title' && !this.morning.open) this.maybePaper();
  }

  /** The key a shared lap goes by: the lap, the name on it, and whether it's sent back. */
  /**
   * A lap the server refused (it didn't replay, or this player had too many that didn't today): kept on this
   * browser so the game never sends it again. The server says no straight away if it did, but a game that keeps
   * asking is what made one refused lap count over and over.
   */
  private refusedLap(best: LapRecord): string | null {
    const [key, reason] = (readLocal('gp.refusedLap') ?? '').split('#');
    return key === `${best.date}|${best.lapMs}|${best.endTick}` ? (reason ?? NO_REPLAY) : null;
  }

  private markRefused(best: LapRecord, reason: string): void {
    writeLocal('gp.refusedLap', `${best.date}|${best.lapMs}|${best.endTick}#${reason}`);
    this.boardMessage = `Not saved · ${reason}`;
    if (reason === NO_REPLAY) this.hud.showBanner('Lap not saved', 'it didn’t replay exactly · sorry', 'bad', 3);
  }

  private shareKey(best: LapRecord): string {
    const back = this.challenge?.beatBy ?? null;
    return `${best.date}|${best.trackHash}|${best.physicsHash}|${best.lapMs}|${best.endTick}|${this.player.name}|${back ?? ''}`;
  }

  /**
   * The link's preview picture for your best lap: the day's print, "you've
   * been challenged!" and your time. Drawn once per lap (and name), ahead of
   * time while the poster is up, so sharing never waits for it.
   */

  private shareCard(best: LapRecord): Promise<string | null> {
    const key = this.shareKey(best);
    let card = this.cards.get(key);
    if (card) return card;
    const car = ERAS[this.circuit.era];
    const name = this.player.name;
    const back = this.challenge?.beatBy ?? null;
    card = drawShareCard({
      era: this.circuit.era,
      palette: posterStyleFor(best.date, this.circuit.era).palette,
      label: back !== null ? 'you’ve been challenged back!' : 'you’ve been challenged!',
      time: shortLap(best.lapMs),
      whose: `${name ? `${name}’s` : 'a friend’s'} lap`.toLowerCase(),
      where:
        back !== null
          ? `${shortLap(back)} faster than yours · ${this.circuit.name.toLowerCase()}`
          : `${this.circuit.name.toLowerCase()} · ${car.name.toLowerCase()} ${car.year}`,
    })
      .then(async (blob) => {
        if (!blob) return null;
        const bytes = new Uint8Array(await blob.arrayBuffer());
        let bin = '';
        for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
        return btoa(bin);
      })
      .catch(() => null);
    this.cards.set(key, card);
    return card;
  }

  /** While the poster offers your lap to share, draw its picture in the background. */
  private prepareShareCard(): void {
    const best = this.session.day.best;
    if (!best || this.eraOverride || this.boardStatus !== 'ready' || this.shared?.sent === this.shareKey(best)) return;
    window.setTimeout(() => {
      const now = this.session.day.best;
      if (now === best && (this.mode === 'title' || this.mode === 'paused')) void this.shareCard(best);
    }, 400);
  }

  private posterInfo(paused: boolean): TitleInfo {
    const car = ERAS[this.circuit.era];
    const ms = msUntilRomeMidnight();
    const today = romeDateString();
    const best = this.session.day.best;
    const ch = this.challenge;
    // Today's board, open and loaded: where you stand on it.
    const open = this.boardStatus === 'ready' && !!this.board;
    const you = open ? (this.board?.you ?? null) : null;
    const race: PosterAction = { id: 'race', label: paused ? 'resume' : 'race' };
    let actions: PosterAction[];
    if (ch?.expired) actions = [{ id: 'today', label: 'race today' }, { id: 'watch', label: ch.watched ? 'watch again' : 'watch the lap' }];
    // The board is a tap away under the medals (and beside the poster on wide screens).
    else if (!ch) actions = [race];
    else if (ch.beatBy !== null) actions = [{ id: 'sendBack', label: 'send it back' }, race];
    else if (!ch.watched && !paused) actions = [{ id: 'watch', label: 'watch the lap' }, race];
    else actions = [race, { id: 'watch', label: 'watch again' }];
    return {
      circuit: this.circuit.name,
      // "tuesday 6 october · liguria": where in Italy the day is, once the settings began.
      day: formatPosterDate(this.circuit.date) + (this.circuit.date >= SETTINGS_FROM ? ` · ${SETTING_NAMES[this.world?.setting ?? settingForDate(this.circuit.date)].toLowerCase()}` : ''),
      // Another day's circuit (a link opened late, yesterday's winner): laps there are practice.
      closes:
        this.circuit.date === today
          ? `closes in ${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`
          : this.circuit.date < today
            ? 'board closed · practice only'
            : null,
      car: { name: car.name, year: car.year, blurb: car.blurb, preview: this.eraOverride !== null },
      terrain: this.circuit.track.terrain,
      crossover: !!this.circuit.track.crossing,
      signature: this.circuit.track.signature,
      style: posterStyleFor(this.circuit.date, this.circuit.era),
      medals: this.circuit.medals,
      won: { bronze: this.session.day.medals.bronze, silver: this.session.day.medals.silver, gold: this.session.day.medals.gold },
      bestMs: best?.lapMs ?? null,
      standing: you && open ? { rank: you.rank, total: this.board?.total ?? 0 } : null,
      drivers: open && this.board ? this.board.total : null,
      saveable: !!best && open && !you && !this.player.name && !this.eraOverride,
      firstTime: this.lifetimeLaps === 0,
      controls: this.controls(),
      pedals: this.touch.pedalKind,
      paused,
      actions,
      challenge: ch ? this.challengeText(ch, paused) : null,
      podium: this.podium && this.circuit.date === today && !this.eraOverride ? this.podiumInfo(this.podium) : null,
      today: open && this.board && !this.eraOverride ? this.todayInfo(this.board) : null,
      // "send it back" already shares it once you've beaten someone's lap.
      // A link points at your lap on today's board, so only today's open board can be shared from.
      share: best && !this.eraOverride && ch?.beatBy == null && this.boardStatus === 'ready' ? { canSend: canSend() } : null,
      notice: this.notice,
      nudge:
        this.challengeBack !== null && !ch && this.circuit.date === today
          ? `your move: set a lap, then send it to ${this.challengeBack || 'your friend'}`
          : null,
      otherDay: this.circuit.date !== today && !params.has('date'),
      // After a few laps, once: enjoying it? (Not over a challenge: that side has enough to say.)
      poll: !ch && !this.eraOverride && this.lifetimeLaps >= POLL_AFTER_LAPS && readLocal('gp.poll') === null,
      lesson: this.lessonTicket(),
      boardButton: this.lifetimeLaps > 0,
      line: this.eraOverride ? null : this.lineOn(),
      shake: this.shakeOn(),
      discordDoor: this.discordPromo(),
      ...(this.signInInfo() ? { signIn: this.signInInfo()! } : {}),
      invite: IN_DISCORD && !!this.discord,
    };
  }

  /**
   * The driving lesson's ticket beside the race button: big while you're new (or keep restarting), smaller
   * once you've finished laps, just the helmet once you've won a medal; none in a preview or over a challenge.
   */
  private lessonTicket(): LessonTicket | null {
    if (this.eraOverride || this.challenge) return null;
    if (this.lessonNudge()) return 'nudge';
    if (this.lifetimeLaps === 0) return 'new';
    return everWonMedal() ? 'small' : 'regular';
  }

  /** Yesterday's podium as the poster shows it: where it was, and the three. */
  private podiumInfo(p: Podium): PodiumInfo {
    const car = isEraId(p.era) ? ERAS[p.era] : null;
    const circuit = generateCircuitName(Rng.fromString(`name|${circuitDate(p.date)}|0`), settingForDate(p.date)).toLowerCase();
    const paper = this.paper !== null && this.paper.date === p.date && (!!this.paper.you || this.paper.away);
    return { where: car ? `${circuit} · ${car.name.toLowerCase()} ${car.year}` : circuit, places: p.places.map((x) => ({ place: x.place, name: x.name, ms: x.lapMs })), paper };
  }

  /** Today's board at a glance: the top three, your next target and you. */
  private todayInfo(b: Board): TodayInfo {
    const best = this.session.day.best;
    const note =
      b.entries.length === 0
        ? 'no times yet · be the first on the board'
        : b.you
          ? null
          : best
            ? 'save your lap to join them'
            : 'set a clean lap to join them, and to unlock the lap of the driver ahead of you';
    return { total: b.total, top: b.entries.slice(0, 3), target: b.target ?? null, you: b.you, note, challenge: !!b.you && this.canChallenge(), scope: this.scopeInfo() };
  }

  /** Whether today's best can go out as a challenge: a lap on today's open board (not a preview, not mid send-back). */
  private canChallenge(): boolean {
    return !!this.session?.day.best && !this.eraOverride && this.boardStatus === 'ready' && this.circuit.date === romeDateString() && this.challenge?.beatBy == null;
  }

  /** Send your lap as a challenge, from the poster, its card or the leaderboard. */
  private challengeNow(from: 'poster' | 'card' | 'board'): void {
    if (this.mode !== 'title' && this.mode !== 'paused') return;
    this.shareLap(this.controls() === 'touch' && canSend() ? 'send' : 'copy', from);
  }

  /** The poll's answer: kept on this browser (it asks once), and sent straight away. */
  private answerPoll(a: PollAnswer): void {
    if (a === 'up' || a === 'down' || a === 'skip') writeLocal('gp.poll', a);
    this.stats.count(POLL_STAT[a]);
    this.stats.send();
  }

  /** A step of a friend's challenge, counted once a person (not old links', not the winner's, not your own link). */
  private step(k: StatKey): void {
    const ch = this.challenge;
    if (!ch || ch.kind !== 'friend' || ch.expired || ch.own || ch.battle || ch.counted.has(k)) return;
    ch.counted.add(k);
    // Already counted on this browser for this link, on another visit: not again.
    if (ch.linkId && !firstForLink(ch.linkId, k)) return;
    this.stats.count(k);
  }

  /**
   * A friend's link opened: their lap to watch and race, and for the stats, a link that reached someone (once a
   * person). The player's own link, opened to check it, counts for nothing.
   */
  private openedLink(id: string, lap: WatchLap): void {
    const took = this.takeChallenge('friend', lap);
    const own = id === this.ownLinkId() || this.isOwnLap(lap.record);
    if (took && this.challenge) {
      this.challenge.linkId = id;
      this.challenge.own = own;
    }
    if (own) return;
    const k: StatKey = took && this.challenge?.expired ? 'oldLinkOpened' : 'linkOpened';
    if (took && firstForLink(id, k)) this.stats.count(k);
    // One more person the link reached, the first time it's opened here (fresh or after its day).
    this.stats.cameByLink(firstForLink(id, 'reached') ? id : null);
    // Racing it and setting a lap makes a challenge with its driver for the day.
    if (took) this.noteVia(id, lap.record.date);
    // Sent on an earlier day: links don't expire, it opens its driver's lap today. Say so.
    if (took && lap.sentOn && lap.sentOn < romeDateString()) this.oldLinkPopup(lap.name, lap.sentOn, lap.lapMs);
  }

  /** A friend's link whose driver has no lap today (yet): today's race, a lap of yours for them to see. */
  private openedNoLap(id: string, n: LinkNoLap): void {
    if (id === this.ownLinkId()) return;
    if (firstForLink(id, 'linkOpened')) this.stats.count('linkOpened');
    this.stats.cameByLink(firstForLink(id, 'reached') ? id : null);
    this.noteVia(id, romeDateString());
    this.oldLinkPopup(n.name, n.sentOn, null);
  }

  /** An old link opened: when it was sent, and what today holds instead (the driver's lap today, or none yet). */
  private oldLinkPopup(name: string, sentOn: string, lapMs: number | null): void {
    const today = romeDateString();
    const who = name || 'your friend';
    const Who = name || 'Your friend';
    const sent = sentOn < today ? `sent ${pastDay(sentOn, today).when}` : `from ${who.toLowerCase()}`;
    const race = { go: lapMs !== null ? 'race it' : 'race today', keep: 'close', onGo: () => this.go(), onKeep: () => undefined };
    if (lapMs !== null) {
      this.screens.showPopup({
        kicker: `a challenge from ${who}`,
        title: sent,
        line: `${Who} has a new lap today: ${shortLap(lapMs)}`,
        note: `That race is over, so you’ll race ${who}’s lap on today’s circuit instead.`,
        ...race,
      });
    } else {
      this.screens.showPopup({
        kicker: `a challenge from ${who}`,
        title: sent,
        line: `${Who} hasn’t raced today yet`,
        note: `Set a lap on today’s circuit, then send ${who} a new challenge.`,
        ...race,
      });
    }
  }

  /** This player's own challenge link: one for good, made the first time they send one ('' before that). */
  private ownLinkId(make = false): string {
    const saved = readLocal('gp.link') ?? '';
    if (SHARE_ID.test(saved)) return saved;
    if (!make) return '';
    const id = newShareId();
    writeLocal('gp.link', id);
    return id;
  }

  /** A friend's link raced today: it goes with this day's laps, and makes a challenge with its driver. */
  private noteVia(id: string, date: string): void {
    const v = this.viaList();
    const ids = v.date === date ? v.ids.filter((x) => x !== id) : [];
    ids.push(id);
    writeLocal('gp.via', JSON.stringify({ date, ids: ids.slice(-VIA_MAX) }));
  }

  /** The friends' links raced on a day (for its laps), or undefined. */
  private viaFor(date: string): string[] | undefined {
    const v = this.viaList();
    return v.date === date && v.ids.length ? v.ids : undefined;
  }

  private viaList(): { date: string; ids: string[] } {
    try {
      const o = JSON.parse(readLocal('gp.via') ?? 'null') as { date?: unknown; ids?: unknown } | null;
      if (o && typeof o.date === 'string' && Array.isArray(o.ids)) return { date: o.date, ids: o.ids.filter((x): x is string => typeof x === 'string' && SHARE_ID.test(x)) };
    } catch {
      // Unreadable: no links raced, as far as anyone can tell.
    }
    return { date: '', ids: [] };
  }

  /**
   * A battle's news: someone you have a challenge with today is ahead of your lap (the board's, or a stats
   * tally's answer). Each is news once (by their lap): a banner while racing, a popup on the poster.
   */
  private battleNews(list: Battle[] | undefined, onPoster = false): void {
    if (!list?.length || this.eraOverride || this.circuit.date !== romeDateString()) return;
    const told = this.battlesTold();
    const b = list.find((x) => !told.includes(`${this.circuit.date}|${x.ref}`));
    if (!b) return;
    const who = b.name || 'a friend';
    if (this.mode === 'running' && !onPoster) {
      if (this.lesson || this.toldLive.has(b.ref)) return;
      this.toldLive.add(b.ref);
      this.hud.showBanner(`${who} beat your lap`, `by ${shortLap(b.gapMs)} · pause to race back`, '', 4.5);
      return;
    }
    if ((!onPoster && this.mode !== 'title' && this.mode !== 'paused') || this.screens.showingPopup || this.screens.askingName || this.lesson) return;
    const mine = this.board?.you?.lapMs ?? null;
    this.stats.count('battleShown');
    this.screens.showPopup({
      kicker: 'your challenge',
      title: `${who.toLowerCase()} beat you`,
      line: `by ${shortLap(b.gapMs)} · ${b.leads ? `${who} leads today` : `their ${shortLap(b.lapMs)}, your ${mine !== null ? shortLap(mine) : '—'}`}`,
      note: b.leads ? `You have a challenge with ${who} today, so you can race that lap until midnight.` : null,
      go: 'take it back',
      keep: 'later',
      onGo: () => {
        this.tellBattle(b);
        void this.takeBattle(b.ref);
      },
      onKeep: () => this.tellBattle(b),
    });
  }

  /** Battles already told about (by day and lap), the newest few. */
  private battlesTold(): string[] {
    try {
      const o = JSON.parse(readLocal('gp.battles') ?? '[]') as unknown;
      return Array.isArray(o) ? o.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }

  private tellBattle(b: Battle): void {
    const told = this.battlesTold();
    told.push(`${this.circuit.date}|${b.ref}`);
    writeLocal('gp.battles', JSON.stringify(told.slice(-30)));
  }

  /** "Take it back": a rival's lap (theirs to race even if it leads: a challenge), raced straight away with its ghost. */
  private async takeBattle(ref: string): Promise<void> {
    const c = this.circuit;
    const lap = await this.api.getBoardLap(c.date, c.trackHash, this.session.basePhysicsHash, ref, this.player.id);
    if (this.circuit !== c) return;
    if (!lap) {
      this.setNotice('That lap isn’t there any more (they set a faster one) · the board is up to date');
      void this.refreshBoard();
      return;
    }
    if (!this.takeChallenge('friend', lap) || !this.challenge) return;
    this.challenge.battle = true;
    this.stats.count('battleTaken');
    // From the pause menu too: a fresh run, the ghost from the line.
    const paused = this.mode === 'paused';
    this.go();
    if (paused) this.restart();
  }

  /**
   * The share nudge ("can they catch you?"): a popup over the poster, with a short clip of a car racing its ghost,
   * asking the player to send their link. Once after their first lap on a board, then at most once a week right
   * after a new best. Never if they sent their link in the last 7 days, if a friend's link brought them today,
   * over another popup (or the name card coming), in the lesson, or after two "not now" in a row.
   */
  private maybeNudge(): void {
    if (this.mode !== 'title' && this.mode !== 'paused') return;
    const today = romeDateString();
    if (this.eraOverride || this.lesson || this.circuit.date !== today || this.boardStatus !== 'ready') return;
    if (this.screens.showingPopup || this.screens.askingName || this.challenge) return;
    const you = this.board?.you ?? null;
    const best = this.session.day.best;
    if (!you || !best || you.lapMs !== best.lapMs) return;
    // The name card comes first (it shows on this pause): the nudge waits for the next poster.
    if (this.madeUpName && !readLocal('gp.madeUpAsked')) return;
    if (this.viaFor(today)) return;
    const sent = readLocal('gp.sentLink');
    if (sent && sent > shiftDate(today, -7)) return;
    const n = this.nudgeState();
    if (n.noes >= 2) return;
    const first = !n.first;
    if (!first && (!this.goodLap || (n.last !== '' && n.last > shiftDate(today, -7)))) return;
    this.goodLap = false;
    this.setNudge({ first: true, last: today });
    this.stats.count('nudgeShown');
    const total = this.board?.total ?? you.rank;
    this.screens.showPopup({
      kicker: first ? 'you’re on today’s board' : you.rank <= 10 ? `p${you.rank} · top 10 today` : 'a new personal best',
      title: 'can they catch you?',
      clip: { src: 'media/ghost-duel.mp4', poster: 'media/ghost-duel.jpg', label: 'A car racing its ghost: the ghost ahead, caught, then passed' },
      line: first ? `Your ${shortLap(best.lapMs)} at ${this.circuit.name}` : `Your new best, ${shortLap(best.lapMs)} · P${you.rank} of ${total}`,
      note: 'Send your link: friends watch your lap, then race it with your car beside them as a ghost.',
      go: 'challenge your friends ↗',
      keep: 'not now',
      onGo: () => {
        this.setNudge({ noes: 0 });
        this.stats.count('nudgeShared');
        this.shareLap(this.controls() === 'touch' && canSend() ? 'send' : 'copy', 'poster');
      },
      onKeep: () => this.setNudge({ noes: n.noes + 1 }),
    });
  }

  private nudgeState(): { first: boolean; last: string; noes: number } {
    try {
      const o = JSON.parse(readLocal('gp.nudge') ?? 'null') as { first?: unknown; last?: unknown; noes?: unknown } | null;
      return { first: o?.first === true, last: typeof o?.last === 'string' ? o.last : '', noes: typeof o?.noes === 'number' ? o.noes : 0 };
    } catch {
      return { first: false, last: '', noes: 0 };
    }
  }

  private setNudge(change: Partial<{ first: boolean; last: string; noes: number }>): void {
    writeLocal('gp.nudge', JSON.stringify({ ...this.nudgeState(), ...change }));
  }

  /**
   * A link has gone out (copied, or sent from the share sheet): what happens next. The first time, three steps on
   * a card (their friend races it; if they're beaten they'll hear it here; until midnight); after that a stamp
   * with one line. On the poster only (racing, the banner's button says it).
   */
  private afterSent(sent: boolean, lapMs: number): void {
    if (this.mode !== 'title' && this.mode !== 'paused') return;
    const time = shortLap(lapMs);
    if (readLocal('gp.sentExplained') === null && !this.screens.showingPopup && !this.screens.askingName) {
      writeLocal('gp.sentExplained', '1');
      this.screens.showPopup({
        kicker: sent ? 'sent ✓' : 'link copied ✓',
        title: sent ? 'now it’s their move' : 'now paste it to a friend',
        steps: [
          `They watch your ${time}, then race it with your car beside them as a ghost.`,
          'Beat you? You’ll hear it here, and can race their lap to take it back.',
          'It’s on until midnight. Then a new circuit, a new race.',
        ],
        go: 'got it',
        onGo: () => undefined,
        onKeep: () => undefined,
      });
      return;
    }
    this.screens.stamp(sent ? 'sent ✓' : 'link copied ✓', `we’ll tell you if anyone beats ${time}`);
  }

  /** "Send it back", from the banner of a lap that beat the challenge: one tap (or C), how it went on the button. */
  private sendBack(say: (label: string) => void): void {
    const how = this.controls() === 'touch' && canSend() ? 'send' : 'copy';
    // A link needs a name for the board: the pause menu asks for it.
    if (!this.player.name) {
      this.pause();
      this.shareLap(how, null);
      return;
    }
    say(how === 'send' ? 'sharing…' : 'copying…');
    this.shareLap(how, null, say);
  }

  /** A lap this browser drove itself: the day's best here, the same to the tick. */
  private isOwnLap(r: LapRecord): boolean {
    const best = this.session.day.best;
    return !!best && best.date === r.date && best.trackHash === r.trackHash && best.lapMs === r.lapMs && best.endTick === r.endTick;
  }

  /**
   * Race on your own: a friend's lap you're racing (from a link, a "Race their ghost" in Discord, a battle) goes,
   * with its ghost; your own best's ghost comes back. A link in the address goes too, so a reload doesn't bring it back.
   */
  private dropChallenge(): void {
    if (!this.challenge) return;
    this.challenge = null;
    this.session.setRival(null);
    this.hud.setRival(null);
    this.syncGold();
    this.stats.count('ghostDropped');
    const u = new URL(location.href);
    if (u.searchParams.has('lap')) {
      u.searchParams.delete('lap');
      history.replaceState(null, '', u);
    }
    if (this.mode === 'title' || this.mode === 'paused') this.showTitle(this.mode === 'paused');
  }

  /** What the poster says about a challenge: whose lap, and what happens next. */
  private challengeText(ch: Challenge, paused: boolean): NonNullable<TitleInfo['challenge']> {
    const t = this.challengeWords(ch, paused);
    // A friend's lap you're racing today: the way to race without it.
    return ch.kind === 'friend' && !ch.expired ? { ...t, drop: `✕ race without ${ch.name ? `${ch.name}’s` : 'their'} ghost` } : t;
  }

  private challengeWords(ch: Challenge, paused: boolean): NonNullable<TitleInfo['challenge']> {
    const who = ch.name || 'a friend';
    const time = shortLap(ch.lapMs);
    const best = this.session.day.best;
    if (ch.beatBy !== null && best) {
      return { kicker: `you beat ${who}`, line: `${shortLap(best.lapMs)} · ${shortLap(ch.beatBy)} faster than ${who}`, hint: 'Send it back and it’s their move.' };
    }
    const medal = topMedal(ch.lapMs, this.circuit.medals);
    const standing = ch.rank !== null && ch.total ? ` · P${ch.rank} of ${ch.total}` : '';
    const line = `${who} · ${time}${medal ? ' ' + medal : ''}${standing}`;
    if (ch.expired) {
      return { kicker: 'this race is over', line, hint: 'You can only watch this lap now · race today’s circuit instead.', intro: ch.watched ? null : this.challengeIntro(ch) };
    }
    const ghost = ch.champion
      ? `${ch.name || 'They'} won yesterday: ${ch.name ? 'their' : 'the'} gold car runs beside you as a ghost · beat ${time}`
      : `${ch.name ? `${ch.name}’s` : 'Their'} car runs beside you as a ghost · beat ${time}`;
    if (paused) return { kicker: 'paused', line, hint: ghost };
    if (ch.watched) return { kicker: 'your turn', line, hint: ghost };
    const whose = ch.name ? `${ch.name}’s` : 'your friend’s';
    return {
      kicker: ch.kind === 'winner' ? 'yesterday’s winner' : `a challenge from ${who}`,
      line,
      hint: `Watch ${whose} lap first, or race it straight away.`,
      intro: this.challengeIntro(ch),
    };
  }

  /**
   * The challenge set out like a race programme, for someone who may never
   * have played: who, what the game is, the circuit and car, the time to beat,
   * the day's board, and the three steps.
   */
  private challengeIntro(ch: Challenge): ChallengeIntro {
    const whose = ch.name ? `${ch.name}’s` : 'your friend’s';
    const time = shortLap(ch.lapMs);
    const car = ERAS[this.circuit.era];
    const t = this.circuit.track;
    const todayDate = romeDateString();
    const today = this.circuit.date === todayDate;
    const closed = this.circuit.date < todayDate;
    const medal = topMedal(ch.lapMs, this.circuit.medals);
    const standing = ch.rank !== null && ch.total ? ` · P${ch.rank} of ${ch.total}` : '';
    const b = this.board;
    if (ch.expired) return this.expiredIntro(ch);
    return {
      label: ch.name ? `${ch.name} has challenged you!` : 'you’ve been challenged!',
      pitch: 'A racing game with a new circuit and a new car every day. Everyone gets the same one; the fastest lap wins.',
      circuit: {
        label: today ? 'today’s circuit' : formatPosterDate(this.circuit.date),
        name: this.circuit.name.toLowerCase(),
        tag: circuitTag(t.signature, !!t.crossing, t.terrain),
        outline: trackOutlineSvg(t),
      },
      car: { label: today ? 'your car' : 'the car', name: car.name.toLowerCase(), year: car.year, blurb: car.blurb.split(/(?<=\.)\s/)[0] },
      beat: { time, who: `${medal ? medal + ' ' : ''}${ch.name || 'your friend'}${standing}` },
      board:
        b && b.entries.length
          ? {
              title: `${today ? 'today' : 'that day'} · ${b.total} ${b.total === 1 ? 'driver' : 'drivers'}`,
              rows: b.entries.slice(0, 3).map((e) => ({ rank: e.rank, name: e.name, time: shortLap(e.lapMs), them: !!ch.name && e.name === ch.name && e.lapMs === ch.lapMs })),
            }
          : null,
      steps: [
        { title: `watch ${whose} lap`, text: `one lap, about ${Math.round(ch.lapMs / 1000)} s · skip any time` },
        { title: 'race the same lap', text: `${ch.name ? `${ch.name}’s` : 'their'} ${ch.champion ? 'gold car (yesterday’s winner)' : 'car'} runs beside you as a ghost` },
        { title: `beat ${time}`, text: closed ? 'that day is over, so it’s practice' : 'then send your lap back' },
      ],
      watch: 'watch the lap',
      race: 'race now',
    };
  }

  /**
   * A friend's link opened after its day: the race is over. Say so, say why,
   * show how that day ended, and point at today's circuit.
   */
  private expiredIntro(ch: Challenge): ChallengeIntro {
    const day = pastDay(this.circuit.date, romeDateString());
    const whose = ch.name ? `${ch.name}’s` : 'your friend’s';
    const time = shortLap(ch.lapMs);
    const car = ERAS[this.circuit.era];
    const t = this.circuit.track;
    const medal = topMedal(ch.lapMs, this.circuit.medals);
    const standing = ch.rank !== null && ch.total ? ` · P${ch.rank} of ${ch.total}` : '';
    const b = this.board;
    return {
      label: ch.name ? `${ch.name} challenged you · ${day.when}` : `you were challenged · ${day.when}`,
      title: 'this race is over',
      pitch: 'Grand Premio is a racing game with a new circuit and a new car every day.',
      circuit: { label: `${day.whose} circuit`, name: this.circuit.name.toLowerCase(), tag: circuitTag(t.signature, !!t.crossing, t.terrain), outline: trackOutlineSvg(t) },
      car: { label: 'the car', name: car.name.toLowerCase(), year: car.year, blurb: car.blurb.split(/(?<=\.)\s/)[0] },
      beat: { label: `${whose} time`, time, who: `${medal ? medal + ' ' : ''}${ch.name || 'your friend'}${standing}` },
      board:
        b && b.entries.length
          ? {
              title: `${day.when.replace(/^on /, '')} · final`,
              rows: b.entries.slice(0, 3).map((e) => ({
                rank: e.rank,
                name: e.rank === 1 ? `${e.name} 🏆` : e.name,
                time: shortLap(e.lapMs),
                them: !!ch.name && e.name === ch.name && e.lapMs === ch.lapMs,
              })),
            }
          : null,
      steps: [],
      note: `The circuit changes every day, so ${day.whose} is closed: you can only watch ${whose} ${time} now. Race today’s circuit and send ${ch.name || 'them'} your lap, or ask them for a new link.`,
      watch: 'watch the lap',
      race: 'race today ▸',
      raceAction: 'today',
    };
  }

  /** Leave an old link's day for today's circuit, with a nudge to challenge them back. */
  private leaveForToday(): void {
    const ch = this.challenge;
    const back = ch?.expired ? ch.name : null;
    void this.goToday().then(() => {
      if (back !== null) {
        this.challengeBack = back;
        this.updatePoster();
      }
    });
  }

  private setNotice(text: string | null): void {
    this.notice = text;
    this.updatePoster();
  }

  /** ?paper=1 has shown it again this visit. */
  private paperAgain = false;

  /** The morning paper (once a visit): the champion's gold car, days in a row, and the paper itself on the poster. */
  private async fetchPaper(date: string): Promise<void> {
    if (!this.earlyPaper && this.paper) return;
    const ask = this.earlyPaper ?? this.api.getPaper(this.player.id);
    this.earlyPaper = null;
    const p = await ask;
    this.paper = p;
    if (this.circuit.date !== date) return;
    this.syncGold();
    this.updatePoster();
  }

  /** Yesterday's winner, on today's circuit: their car is made of gold for the day. */
  private champion(): boolean {
    return this.paper?.you?.rank === 1 && !!this.circuit && this.circuit.date === romeDateString() && !this.eraOverride;
  }

  /** The lap being watched (not laps watched together) is yesterday's winner's: its car is gold. */
  private watchedChampion(): boolean {
    return this.boardView?.champion === true || (!this.boardView && this.challenge?.champion === true);
  }

  /**
   * Which cars are gold now: the one on screen (yours if you're yesterday's winner, or the lap watched if it's
   * the winner's), and the ghost (your own best, or the rival raced if that's the winner's lap).
   */
  private syncGold(): void {
    if (!this.world || !this.circuit) return;
    const mine = this.champion();
    const pw = this.group;
    const live = this.liveWatch;
    const car = this.mode === 'watching' ? (live ? live.focusCar?.gold === true : pw ? pw.laps[pw.focus].gold === true : this.watchedChampion()) : mine;
    const ghost = this.challenge && !this.challenge.expired ? this.challenge.champion === true : mine;
    this.world.setGold(car, ghost);
    // Yesterday's podium says nothing about it: the car is simply gold, as it was that day.
    this.hud.setWatchBadge(this.mode === 'watching' && car && !live && !pw?.podium && !(pw ? pw.laps[pw.focus].own : this.boardView?.own) ? 'yesterday’s winner · in gold today' : null);
  }

  /** Days in a row with a lap, today's counted once there is one (null when not on today's circuit). */
  private streakDays(): number | null {
    const p = this.paper;
    if (!p || this.circuit.date !== romeDateString() || this.eraOverride) return null;
    const raced = this.session.day.best !== null || !!this.board?.you;
    return (p.you ? p.streak : 0) + (raced ? 1 : 0);
  }

  /** What the paper says about the two days: yesterday's circuit and car, today's. */
  private paperDays(p: Paper): PaperDays {
    const today = romeDateString();
    const car = (era: string): string => (isEraId(era) ? `${ERAS[era].name} ${ERAS[era].year}` : '');
    const c = this.circuit;
    return {
      dateLabel: `${formatPosterDate(today)} ${today.slice(0, 4)}`,
      issue: issueOf(today),
      yesterday: { circuit: generateCircuitName(Rng.fromString(`name|${circuitDate(p.date)}|0`), settingForDate(p.date)), region: SETTING_NAMES[settingForDate(p.date)], car: car(p.era) },
      today: { circuit: c.name, region: SETTING_NAMES[settingForDate(today)], car: car(c.era) },
    };
  }

  /**
   * The morning paper over the poster: the first poster of the day after a race (or, missing a day, after a
   * week with races), never over a friend's link. ?paper=1 shows it again (the maker's own copies), and so does
   * opening the game from the paper's "Read the paper" in Discord.
   */
  private maybePaper(): boolean {
    const p = this.paper;
    const today = romeDateString();
    if (!p || (!p.you && !p.away) || this.mode !== 'title' || this.circuit.date !== today || this.eraOverride || this.challenge || this.lesson || this.liveLaunch) return false;
    const again = ((DEV_HOST && params.get('paper') === '1') || this.discord?.paper === true) && !this.paperAgain;
    if (readLocal('gp.paper') === today && !again) return false;
    this.paperAgain = true;
    writeLocal('gp.paper', today);
    this.stats.count('paperShown');
    this.showPaper();
    return true;
  }

  /** The morning paper over the poster: the first poster of the day, or again from yesterday's podium. */
  private showPaper(): void {
    const p = this.paper;
    if (!p || this.morning.open) return;
    const words = paperWords(p, this.paperDays(p));
    this.paperClip = null;
    // Il paddock, until tapped once (inside Discord only with its session: a plain link does nothing in the frame).
    const join = readLocal(PADDOCK_KEY) || (IN_DISCORD && !this.discord) ? undefined : () => this.joinPaddock();
    this.morning.show(words, { share: (say) => this.sharePaper(say), close: () => this.screens.show('main'), join });
    this.paperPhoto = pressPhoto(figurePictures(podiumFigures(p.podium.map((x) => x.name))));
    void this.paperPhoto.then(async (src) => {
      this.morning.setPhoto(src);
      // The share picture, ready before a tap asks for it (a phone's share sheet must open inside the tap).
      const blob = await clippingPicture(words, src, location.host);
      if (blob) this.paperClip = new File([blob], 'gazzetta.jpg', { type: 'image/jpeg' });
    });
  }

  /**
   * Il paddock: the server's invite (Discord's own prompt inside Discord, a new tab outside), from the paper's line or
   * the "race together" card. Either way the paper stops offering it.
   */
  private joinPaddock(from: 'paper' | 'card' = 'paper'): void {
    writeLocal(PADDOCK_KEY, '1');
    this.stats.count(from === 'card' ? 'paddockJoinCard' : 'paddockJoin');
    if (this.discord) openOutside(this.discord, PADDOCK_INVITE);
    else window.open(PADDOCK_INVITE, '_blank', 'noopener');
  }

  /** The paper's share: its clipping and a line of text (the share sheet on a phone; copied on a computer). */
  private sharePaper(say: (label: string) => void): void {
    const p = this.paper;
    if (!p) return;
    const text = paperShareText(p, this.paperDays(p), `${location.origin}${location.pathname}`);
    const file = this.paperClip;
    const done = (label: string): void => {
      say(label);
      this.stats.count('paperShared');
    };
    // On a phone, the share sheet (with the clipping when it can take a picture); on a computer, copied.
    const phone = this.controls() === 'touch' && canSend();
    if (phone && file && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
      navigator.share({ files: [file], text }).then(
        () => done('sent ✓'),
        (e: { name?: string }) => {
          if (e?.name !== 'AbortError') void copyText(text).then((ok) => ok && done('copied ✓'));
        },
      );
      return;
    }
    if (phone) {
      void sendText(text).then((r) => r === 'sent' && done('sent ✓'));
      return;
    }
    void copyText(text).then((ok) => (ok ? done('copied ✓') : say('couldn’t copy')));
  }

  private async fetchPodium(date: string): Promise<void> {
    // The one asked for as the page started, if it's still to come; otherwise ask now.
    const ask = this.earlyPodium ?? this.api.getPodium(shiftDate(date, -1));
    this.earlyPodium = null;
    const p = await ask;
    if (this.circuit.date !== date) return;
    this.podium = p;
    this.updatePoster();
  }

  /**
   * Race someone's lap: it must replay exactly on this circuit and car, then
   * it's the ghost and the gap, and the poster offers to watch it first.
   */
  private takeChallenge(kind: Challenge['kind'], lap: WatchLap): boolean {
    const r = lap.record;
    const fits = r.date === this.circuit.date && r.trackHash === this.circuit.trackHash && this.session.physicsOf(r) !== null;
    if (!fits || !this.session.setRival(r)) {
      this.setNotice('That lap was set on an older version of this circuit · it can’t be replayed');
      return false;
    }
    const expired = kind === 'friend' && this.circuit.date < romeDateString();
    this.challenge = { kind, name: lap.name, lapMs: lap.lapMs, record: r, rank: lap.rank, total: lap.total, watched: false, beatBy: null, expired, counted: new Set(), champion: lap.champion === true };
    this.checkBeaten();
    this.hud.setRival(lap.name || 'a friend', lap.lapMs, lap.champion === true);
    this.syncGold();
    this.notice = null;
    if (this.mode === 'title' || this.mode === 'paused') this.showTitle(this.mode === 'paused');
    return true;
  }

  /** Your best against the challenge: beaten, and by how much. */
  private checkBeaten(): void {
    const ch = this.challenge;
    const best = this.session.day.best;
    if (ch && best && best.lapMs < ch.lapMs) ch.beatBy = ch.lapMs - best.lapMs;
  }

  /** Watch the challenge's lap: the camera follows it round with its timer running. */
  private watch(): void {
    const bv = this.boardView;
    const ch = this.challenge;
    if (this.group && !this.group.podium) this.group = null;
    let pw = this.group;
    const lap = bv ?? ch ?? (pw ? pw.laps[pw.focus] : null);
    if (!lap || this.mode === 'loading') return;
    void this.audio.start();
    this.audio.setCarSounds(true);
    (document.activeElement as HTMLElement | null)?.blur?.();
    // Your own best on this circuit runs beside it (not when the lap watched is yours).
    const mine = bv?.own ? null : this.ownLapBeside(pw ? pw.laps.map((l) => l.record) : [lap.record]);
    if (mine) {
      const you = { place: 0, name: 'you', lapMs: mine.lapMs, record: mine, own: true, gold: !pw?.podium && this.champion() };
      if (pw) pw.laps.push(you);
      else pw = this.group = { podium: false, laps: [{ place: 0, name: bv ? bv.name : (ch?.name ?? ''), lapMs: lap.lapMs, record: lap.record, gold: this.watchedChampion() }, you], focus: 0, runners: [], tracks: [] };
    }
    if (pw) {
      // The podium's laps run together: the same run-up for all, so they cross the line at the same moment.
      const rt = this.circuit.runtime;
      // Each with the car it was driven with (a day whose car changed mid-race has laps with both).
      const ph = (l: { record: LapRecord }): PhysicsTuning => this.session.physicsOf(l.record) ?? this.circuit.physics;
      const runUp = Math.min(RUN_UP, ...pw.laps.map((l) => Spectator.lapStartTick(rt, ph(l), l.record)));
      pw.runners = pw.laps.map((l) => new Spectator(rt, ph(l), l.record, runUp));
      pw.tracks = pw.laps.map((l) => replayRecord(rt, ph(l), l.record, true).ghost ?? null);
      this.spectator = pw.runners[pw.focus];
    } else this.spectator = new Spectator(this.circuit.runtime, this.session.physicsOf(lap.record) ?? this.circuit.physics, lap.record);
    this.watchBannerShown = false;
    // Replays show the lap as driven, without the beginner's line and braking zones (showTitle puts them back).
    this.world.lesson?.setLine(false);
    this.world.lesson?.showEvents('none');
    if (!bv && !pw?.podium) this.step('linkWatched');
    this.mode = 'watching';
    this.screens.hide();
    this.hud.setTitleMode(false);
    document.body.classList.remove('title-mode');
    document.body.classList.add('watching');
    // Leaderboard laps, yesterday's winner and old links are only to watch; a challenge's lap is yours to race next.
    const words: WatchWords | undefined = pw?.podium
      ? this.podiumWords()
      : bv
        ? { text: 'from the leaderboard · just watching', skip: 'back to the leaderboard ▸', turn: `P${bv.rank} today`, after: 'back to the leaderboard ▸' }
        : ch?.kind === 'winner'
          ? WATCH_ONLY
          : ch?.expired
            ? { text: `${pastDay(this.circuit.date, romeDateString()).whose} race · just for watching`, skip: 'race today ▸', turn: 'your move!', after: 'race today ▸' }
            : undefined;
    if (pw) pw.words = words;
    this.hud.setWatching(this.whoseLap(), this.watchKeys(), words);
    this.hud.setPeers(pw ? this.groupPeers() : null);
    this.hud.setOwnGhost(mine ? !this.ownGhostOff : null);
    this.syncGold();
    if (mine && !this.ownGhostOff) this.stats.count('ownGhost');
    this.hud.setSectors([null, null, null]);
    this.world.chase.snap();
    this.acc = 0;
    this.last = performance.now();
  }

  /** The way out of a replay without touching ("or press Esc"), or null on a phone. */
  private watchKeys(): string | null {
    const way = this.controls();
    return way === 'pad' ? 'or press B' : way === 'keys' ? 'or press Esc' : null;
  }

  /** What the watch card says over a podium lap: whose place it was. */
  private podiumWords(): WatchWords {
    const pw = this.group;
    const place = pw ? ordinal(pw.laps[pw.focus].place) : '';
    const text = pw && pw.laps.length > 1 ? `${place} yesterday · the others run as ghosts` : `${place} yesterday · just for watching`;
    return { text, skip: 'back to today ▸', turn: `${place} yesterday`, after: 'back to today ▸' };
  }

  /** What the watch card says now: the podium's place, or (following your own lap) whose runs beside it. */
  private groupWords(): WatchWords | undefined {
    const pw = this.group;
    if (!pw) return undefined;
    if (pw.podium && !pw.laps[pw.focus].own) return this.podiumWords();
    if (!pw.laps[pw.focus].own) return pw.words;
    const theirs = pw.laps.find((l) => !l.own);
    // A podium's place or a board lap's rank is theirs: at the line it's your lap (a friend's challenge still hands over).
    const base = pw.words ?? RACE_NEXT;
    const text = `${theirs?.name ? `${theirs.name}’s` : 'their'} lap runs beside yours as a ghost`;
    return { ...base, text, turn: base === RACE_NEXT ? base.turn : 'your lap' };
  }

  /** The laps watched together, for the watch card's switcher (your own only while its ghost shows). */
  private groupPeers(): { key: number; place: number | null; name: string; own: boolean; on: boolean }[] {
    const pw = this.group;
    if (!pw) return [];
    return pw.laps
      .map((l, i) => ({ key: i, place: pw.podium && !l.own ? l.place : null, name: l.own ? 'you' : l.name || 'your friend', own: !!l.own, on: i === pw.focus }))
      .filter((p) => !(p.own && this.ownGhostOff));
  }

  /**
   * Your best on the circuit being watched, to run beside the replay as a ghost: kept on this device, driven
   * with a car the replay knows, and not one of the laps watched.
   */
  private ownLapBeside(watched: LapRecord[]): LapRecord | null {
    const c = this.circuit;
    const board = this.boardOwn?.record;
    const fromBoard = board && board.date === c.date && board.trackHash === c.trackHash ? board : null;
    return ownLapFor([this.session.day.best, fromBoard], watched, (r) => this.session.canReplay(r));
  }

  /**
   * Your lap on today's board, when this device has none as good (you raced on another one): fetched once, to
   * run beside replays.
   */
  private async fetchBoardOwn(): Promise<void> {
    const you = this.board?.you;
    const best = this.session.day.best;
    if (!you?.ref || this.boardStatus !== 'ready' || (best && best.lapMs <= you.lapMs) || this.boardOwn?.ref === you.ref) return;
    const c = this.circuit;
    const mine = { ref: you.ref, record: null as LapRecord | null };
    this.boardOwn = mine;
    const lap = await this.api.getBoardLap(c.date, c.trackHash, this.session.basePhysicsHash, you.ref, this.player.id);
    if (this.circuit === c && this.boardOwn === mine) {
      mine.record = lap?.record ?? null;
      this.adoptBoardOwn();
    }
  }

  /**
   * Your lap on today's board, faster than this device's best (raced on another device, or inside Discord),
   * becomes this device's best: the one the poster shows, your ghost races and your link sends. Only on the
   * poster between races (mid-race, or paused in one, the ghost would jump): the next poster does it.
   */
  private adoptBoardOwn(): void {
    const own = this.boardOwn?.record;
    if (!own || (this.mode !== 'title' && this.mode !== 'loading') || this.eraOverride || this.autopilot) return;
    if (!this.session.adoptBest(own)) return;
    this.hud.setBest(own.lapMs);
    this.updatePoster();
    this.renderBoard();
  }

  /** The chip on the watch card: your ghost hidden or shown again (and the camera back on their lap). */
  private toggleOwnGhost(): void {
    this.ownGhostOff = !this.ownGhostOff;
    writeLocal('gp.ownGhost', this.ownGhostOff ? 'off' : 'on');
    if (this.ownGhostOff) this.stats.count('ownGhostHidden');
    const pw = this.group;
    if (pw && this.ownGhostOff && pw.laps[pw.focus].own) this.followLap(pw.laps.findIndex((l) => !l.own));
    this.hud.setOwnGhost(!this.ownGhostOff);
    this.hud.setPeers(this.groupPeers());
  }

  /** "camilo's", "your friend's", or "your" (your own lap from the leaderboard). */
  private whoseLap(): string {
    const pw = this.group;
    if (pw?.laps[pw.focus].own) return 'your';
    if (pw?.podium) return `${pw.laps[pw.focus].name}’s`;
    const bv = this.boardView;
    if (bv) return bv.own ? 'your' : `${bv.name}’s`;
    const name = this.challenge?.name;
    return name ? `${name}’s` : 'your friend’s';
  }

  /** The replay is over (or skipped): your turn, or after yesterday's winner, back to today. */
  private endWatch(): void {
    if (this.mode !== 'watching') return;
    if (this.liveWatch) {
      this.endLive();
      return;
    }
    this.spectator = null;
    this.hud.setWatching(null);
    this.hud.setPeers(null);
    this.hud.setOwnGhost(null);
    this.hud.setGhostTags([]);
    this.hud.setDelta(null);
    document.body.classList.remove('watching');
    // Yesterday's podium: back to today.
    const podium = this.group?.podium;
    this.group = null;
    this.hud.setWatchBadge(null);
    if (podium) {
      void this.goToday();
      return;
    }
    // A leaderboard lap: back to the leaderboard (and to your paused lap, if there was one).
    const bv = this.boardView;
    if (bv) {
      this.boardView = null;
      this.world.chase.snap();
      this.mode = bv.resume ? 'paused' : 'title';
      this.showTitle(bv.resume);
      this.screens.openBoard();
      return;
    }
    if (this.challenge?.kind === 'winner') {
      void this.goToday();
      return;
    }
    if (this.challenge?.expired) {
      this.leaveForToday();
      return;
    }
    if (this.challenge) this.challenge.watched = true;
    this.session.restart();
    this.world.chase.snap();
    this.mode = 'title';
    this.showTitle(false);
  }

  /** A row on the board: fetch its lap, then watch it. */
  private async watchBoardLap(e: BoardEntry): Promise<void> {
    if (!e.ref || this.busy) return;
    // Someone you have a challenge with today: their lap is yours to race (and take back).
    if (e.rival && !e.you) {
      void this.audio.start();
      void this.takeBattle(e.ref);
      return;
    }
    // Still inside the tap: phones only let sound start then.
    void this.audio.start();
    const c = this.circuit;
    const lap = await this.api.getBoardLap(c.date, c.trackHash, this.session.basePhysicsHash, e.ref, this.player.id, this.boardGuild());
    if (this.circuit !== c || this.busy) return;
    if (!lap) {
      this.boardMessage = 'That lap isn’t yours to watch any more (the board moved) · refreshed';
      void this.refreshBoard();
      return;
    }
    if (!this.session.canReplay(lap.record)) {
      this.boardMessage = 'That lap was set on an older version of this circuit · it can’t be replayed';
      this.renderBoard();
      return;
    }
    this.boardView = { name: lap.name, own: e.you, rank: e.rank, lapMs: lap.lapMs, record: lap.record, resume: this.mode === 'paused', champion: lap.champion === true };
    this.stats.count('replays');
    this.watch();
  }

  /**
   * Inside Discord: signed in through Discord as their Discord player (their name, no name to type), on their
   * server's board. If it fails, the game runs as a guest on the global board, as on the web.
   */
  private async signInDiscord(): Promise<void> {
    this.screens.loading('signing in with Discord…');
    const d = await startDiscord(this.player.id).catch((e: unknown) => {
      console.warn('Discord sign-in failed', e);
      return null;
    });
    this.screens.loading();
    if (!d) {
      this.stats.count('discordGuest');
      return;
    }
    this.discord = d;
    this.player = { id: d.player.id, name: d.player.name };
    savePlayer(this.player);
    this.stats.count('discordVisits');
    const guild = d.guild;
    const channel = d.channel;
    if (guild) {
      this.lapPoster = new LapPoster(
        () => ({ player: this.player.id, guild: guild.id, channel }),
        () => this.ownLinkId(true),
      );
      // The server's room: who's driving now (to watch them), and who's watching you.
      const live = new LiveLink(
        () => {
          const u = new URL('api/live', location.href);
          u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
          u.search = new URLSearchParams({
            guild: guild.id,
            player: this.player.id,
            name: this.player.name,
            ...(channel ? { channel } : {}),
            ...(this.liveShown ? {} : { announce: '0' }),
          }).toString();
          return u.href;
        },
        () => ({ drive: this.liveDrive(), packed: this.session?.packed ?? [] }),
      );
      live.onRoster = (r) => this.liveRoster(r);
      live.onInputs = (key, run, from, d) => {
        this.liveWatch?.feed(key, run, from, d);
        this.together?.feed(key, run, from, d);
      };
      this.live = live;
      live.start();
      if (d.live) {
        this.liveLaunch = { ...d.live, at: performance.now() };
        window.setTimeout(() => this.followLaunch(true), LAUNCH_WAIT_MS);
      }
    }
  }

  /**
   * Opened from "Watch" or "Race with" on the channel's "on track now": once the poster's up and the room has said
   * who's driving, that driver (by key, or by name if they've come back since), watched or raced with. Gone by
   * then (or `late`: the room never said): the poster says so.
   */
  private followLaunch(late = false): void {
    const want = this.liveLaunch;
    const r = this.live?.roster;
    if (!want || this.mode !== 'title' || !this.circuit || this.morning.open || (!r && !late)) return;
    this.liveLaunch = null;
    const drivers = r ? r.drivers.filter((d) => d.key !== r.you && this.physicsForDay(d.day)) : [];
    const d = drivers.find((x) => x.key === want.key) ?? drivers.find((x) => x.name === want.name);
    this.stats.count(want.mode === 'watch' ? 'liveLaunchWatch' : 'liveLaunchRace');
    if (!d) {
      this.setNotice(`${want.name || 'They'} ${want.name ? 'has' : 'have'} left the track · here’s today’s circuit`);
      return;
    }
    if (want.mode === 'watch') this.watchLive(d.key);
    else {
      this.raceWith = { key: d.key, name: d.name };
      this.go();
    }
  }

  /** The Discord server whose board is shown (null: the global board, as on the web). */
  private boardGuild(): string | null {
    return this.discord?.guild && this.boardScope === 'server' ? this.discord.guild.id : null;
  }

  /** How you're playing: with a controller (once one's been used), on a phone, or with keys. */
  private controls(): 'pad' | 'touch' | 'keys' {
    if (this.padSeen || this.input.lastDevice === 'gamepad') return 'pad';
    return isTouchDevice() ? 'touch' : 'keys';
  }

  private controlsHint(): string {
    const way = this.controls();
    return way === 'pad' ? 'Y restart · Start pause' : way === 'keys' ? `R restart · M mute${DEV ? ' · F1 dev' : ''}` : '';
  }

  /** A controller's A on the poster: whatever its big button does. */
  private primaryAction(): void {
    const view = this.screens.currentView;
    if (view === 'how' || view === 'feedback') {
      this.screens.show('main');
      return;
    }
    // A friend's challenge (fresh or old): its big button is "watch the lap".
    if (view === 'challenge') {
      this.watch();
      return;
    }
    const first = this.posterInfo(this.mode === 'paused').actions[0]?.id;
    if (first === 'watch') this.watch();
    else if (first === 'today') this.leaveForToday();
    // Race or resume ("send it back" wants a tap: sharing needs a touch or a click).
    else this.go();
  }

  /** Loading a circuit or watching a replay: no new replay starts now. */
  private get busy(): boolean {
    return this.mode === 'loading' || this.mode === 'watching';
  }

  /**
   * One of yesterday's podium laps, on yesterday's circuit: followed by the camera, with the other two
   * running alongside as ghosts (from the winner's, the others are right behind).
   */
  private async watchPodium(place: number): Promise<void> {
    const p = this.podium;
    if (!p || this.busy) return;
    // Still inside the tap: phones only let sound start then.
    void this.audio.start();
    const laps = await Promise.all(p.places.map((x) => this.api.getBoardLap(p.date, p.trackHash, p.physicsHash, x.ref, this.player.id)));
    const at = p.places.findIndex((x) => x.place === place);
    if (at < 0 || !laps[at]) {
      this.setNotice('Couldn’t fetch that lap · try again');
      return;
    }
    await this.load(p.date, 0);
    // Every lap must replay exactly on this circuit and car; any that doesn't is left out.
    const c = this.circuit;
    const fits = (l: WatchLap | null): l is WatchLap =>
      !!l && l.record.date === c.date && l.record.trackHash === c.trackHash && this.session.physicsOf(l.record) !== null && this.session.canReplay(l.record);
    const kept = p.places.map((x, i) => ({ x, lap: laps[i] })).filter((k): k is { x: (typeof p.places)[number]; lap: WatchLap } => fits(k.lap));
    const focus = kept.findIndex((k) => k.x.place === place);
    if (focus < 0) {
      this.setNotice('That lap was set on an older version of this circuit · it can’t be replayed');
      return;
    }
    // The car that raced in gold that day (its driver won the day before) is gold again.
    this.group = {
      podium: true,
      laps: kept.map((k) => ({ place: k.x.place, name: k.lap.name || k.x.name, lapMs: k.lap.lapMs, record: k.lap.record, gold: k.lap.champion === true })),
      focus,
      runners: [],
      tracks: [],
    };
    this.stats.count('winnerWatched');
    this.watch();
  }

  /**
   * Driving (or paused mid-run) on today's circuit with the day's own car, as the room should hear it; null
   * otherwise (the poster, the lesson, watching, a tuned car, another day).
   */
  private liveDrive(): LiveDrive | null {
    const s = this.session;
    if (!s || !this.circuit || this.lesson || this.autopilot || this.shotAt >= 0 || this.eraOverride) return null;
    if (this.mode !== 'running' && this.mode !== 'paused') return null;
    const car = physicsHash(s.physics);
    if (car !== s.basePhysicsHash || this.circuit.date !== romeDateString()) return null;
    const best = s.day.best;
    return {
      run: this.liveRun,
      day: `${this.circuit.date}|${this.circuit.trackHash}|${car}`,
      lap: s.state.timing.lapIndex,
      paused: this.mode === 'paused',
      best: best ? { ms: best.lapMs, sectors: best.sectorMs.slice() } : null,
      gold: this.champion(),
    };
  }

  /** A room driver's car, if they're on this circuit (with today's car, or the one it began the day with). */
  private physicsForDay(day: string): PhysicsTuning | null {
    const [date, track, car] = day.split('|');
    if (!this.session || !this.circuit || date !== this.circuit.date || track !== this.circuit.trackHash) return null;
    return this.session.physicsByHash(car);
  }

  /** The room changed: who's on track (the poster's strip, today's board), who's watching you, the cars you watch. */
  private liveRoster(r: LiveRoster): void {
    const drivers = r.drivers.filter((d) => d.key !== r.you && this.physicsForDay(d.day));
    // Raced with since "Race with", and back since (a new visit, a new key): the same driver, by name.
    const w = this.raceWith;
    if (w && !drivers.some((d) => d.key === w.key)) w.key = drivers.find((d) => d.name === w.name)?.key ?? w.key;
    this.liveList = drivers.map((d) => ({ key: d.key, name: d.name, status: d.paused ? 'paused' : d.lap > 0 ? `lap ${d.lap}` : 'out lap', paused: d.paused }));
    this.screens.setLive(this.liveList, this.friendsOn);
    this.hud.setWatchers(watchingYou(r));
    // Racing: the others' cars on your track (gone once you're not).
    if (this.liveDrive()) {
      const link = this.live;
      this.together ??= new LiveWatch(this.circuit.runtime, (day) => this.physicsForDay(day), '', undefined, () => link?.need(), RACING);
      this.together.roster(drivers);
    } else this.together = null;
    if (this.liveWatch) {
      this.liveWatch.roster(drivers);
      this.liveChips();
      this.syncGold();
    }
    this.followLaunch();
  }

  /** Watch a friend driving right now (from the poster's strip or today's board). */
  private watchLive(key: string): void {
    const link = this.live;
    const r = link?.roster;
    if (!link || !r || (this.mode !== 'title' && this.mode !== 'paused')) return;
    const drivers = r.drivers.filter((d) => d.key !== r.you && this.physicsForDay(d.day));
    if (!drivers.some((d) => d.key === key)) return;
    this.liveFromPause = this.mode === 'paused';
    const w = new LiveWatch(this.circuit.runtime, (day) => this.physicsForDay(day), key, (n) => this.liveNews(n), () => link.need());
    w.roster(drivers);
    this.liveWatch = w;
    this.stats.count('liveWatch');
    void this.audio.start();
    this.audio.setCarSounds(true);
    (document.activeElement as HTMLElement | null)?.blur?.();
    // As a replay: their driving, without the beginner's line and braking zones (showTitle puts them back).
    this.world.lesson?.setLine(false);
    this.world.lesson?.showEvents('none');
    this.mode = 'watching';
    this.screens.hide();
    this.hud.setTitleMode(false);
    document.body.classList.remove('title-mode');
    document.body.classList.add('watching');
    this.hud.setWatching('', this.watchKeys(), RACE_NEXT);
    this.hud.setOwnGhost(null);
    this.liveFocus(key);
    this.acc = 0;
    this.last = performance.now();
  }

  /** Follow this driver: their name in the corner, their best and place on the right, the others as chips. */
  private liveFocus(key: string): void {
    const w = this.liveWatch;
    const car = w?.cars.get(key);
    if (!w || !car) return;
    window.clearTimeout(this.liveGoneTimer);
    w.focus = key;
    this.live?.watch(key);
    this.spectator = car.sp;
    this.hud.setLiveWatch({ name: car.name, text: this.liveLine(), skip: this.liveFromPause ? 'back to my race ▸' : 'race ▸' });
    this.hud.setBest(car.best?.ms ?? null);
    this.hud.setRank(this.liveRank(car.name));
    this.hud.setSectors(car.sectors);
    this.hud.setDelta(null);
    this.liveChips();
    this.syncGold();
    this.world.chase.snap();
  }

  /** The other drivers as chips beside the way out (a tap follows them). */
  private liveChips(): void {
    const w = this.liveWatch;
    if (!w) return;
    const others = w.order.filter((k) => k !== w.focus).flatMap((k) => w.cars.get(k) ?? []);
    this.livePeers = others.map((c) => c.key);
    this.hud.setPeers(
      others.map((c, i) => ({ key: i, place: null, name: c.paused ? `${c.name} · paused` : c.name, own: false, on: false })),
      1,
    );
  }

  /** Under their name: what they're up to. */
  private liveLine(): string {
    const w = this.liveWatch;
    const car = w?.focusCar;
    if (!w || !car) return '';
    if (car.paused) return 'paused';
    if (w.joining) return 'joining…';
    const lap = car.sp.state.timing.lapIndex;
    return lap > 0 ? `lap ${lap}` : 'out lap';
  }

  /** Their place on the board shown (the server's), by name. */
  private liveRank(name: string): string | null {
    const b = this.board;
    const e = b ? [...b.entries, ...(b.you ? [b.you] : [])].find((x) => x.name === name) : null;
    return b && e ? `P${e.rank} of ${b.total}` : null;
  }

  /** The next driver to follow: someone driving (not paused) first. */
  private liveNext(): LiveCar | null {
    const w = this.liveWatch;
    if (!w) return null;
    const others = w.order.filter((k) => k !== w.focus).flatMap((k) => w.cars.get(k) ?? []);
    return others.find((c) => !c.paused) ?? others[0] ?? null;
  }

  /** What happens to the driver followed: as their own screen would show it (the game's banners and lap card). */
  private liveNews(n: LiveNews): void {
    const name = n.car.name;
    switch (n.type) {
      case 'restart':
        // Back to the grid: one cut, nothing flashes.
        this.spectator = n.car.sp;
        this.world.chase.snap();
        this.hud.setSectors([null, null, null]);
        this.hud.setDelta(null);
        this.hud.showBanner(`${name} restarted`, 'a fresh lap', '', 2.4);
        break;
      case 'sector':
        this.hud.setSectors(n.car.sectors);
        break;
      case 'lap': {
        this.hud.setSectors(n.car.sectors);
        const best = n.valid && (n.best === null || n.ms < n.best);
        this.hud.showLap({ lapIndex: 0, ms: n.ms, valid: n.valid, sectorMs: n.sectorMs, isPB: best, previousBestMs: n.best, medals: [], verified: null, tuned: false, practice: false }, n.car.sectors);
        if (best) {
          this.hud.showBanner(`new best for ${name}`, shortLap(n.ms), 'gold', 3);
          this.hud.setBest(n.ms);
          this.audio.cheer(2);
        }
        break;
      }
      case 'invalid':
        this.hud.showBanner('Lap invalid', 'track limits · all four wheels off', 'bad', 2.6);
        break;
      case 'barrier':
        this.audio.thud(n.speed);
        break;
      case 'paused': {
        const next = this.liveNext();
        this.hud.showBanner(`${name} paused`, 'their car waits where it stopped', '', 6, next ? { label: `watch ${next.name} ▸`, onTap: () => this.liveFocus(next.key) } : undefined);
        break;
      }
      case 'gone':
        this.hud.showBanner(`${name} stopped driving`, '', '', 3);
        window.clearTimeout(this.liveGoneTimer);
        this.liveGoneTimer = window.setTimeout(() => {
          const next = this.liveNext();
          if (next) this.liveFocus(next.key);
          else this.endWatch();
        }, 3000);
        break;
    }
  }

  /** Done watching live: back to the poster, or to your paused race. */
  private endLive(): void {
    const fromPause = this.liveFromPause;
    this.dropLive();
    this.world.chase.snap();
    this.mode = fromPause ? 'paused' : 'title';
    this.showTitle(fromPause);
  }

  /** Watching live stops (back to the poster, a new day): the HUD yours again. */
  private dropLive(): void {
    if (!this.liveWatch) return;
    window.clearTimeout(this.liveGoneTimer);
    this.liveWatch = null;
    this.live?.watch(null);
    this.spectator = null;
    this.hud.setLiveWatch(null);
    this.hud.setWatching(null);
    this.hud.setPeers(null);
    this.hud.setGhostTags([]);
    this.hud.setDelta(null);
    this.hud.setBest(this.session.day.best?.lapMs ?? null);
    this.hud.setSectors(this.session.lapSectors);
    this.renderBoard();
    document.body.classList.remove('watching');
    this.syncGold();
  }

  /** Laps watched together: follow another of them (the one followed until now runs on as a ghost). */
  private followLap(i: number): void {
    if (this.liveWatch) {
      const key = this.livePeers[i];
      if (key) this.liveFocus(key);
      return;
    }
    const pw = this.group;
    if (!pw || this.mode !== 'watching') return;
    const sp = pw.runners[i];
    if (i < 0 || i === pw.focus || !sp || sp.done) return;
    pw.focus = i;
    this.spectator = sp;
    if (pw.laps[i].own) this.stats.count('ownGhostFollowed');
    this.syncGold();
    this.hud.setWatching(this.whoseLap(), this.watchKeys(), this.groupWords());
    this.hud.setPeers(this.groupPeers());
    this.watchBannerShown = false;
    this.world.chase.snap();
  }

  /** Back to today's circuit (from a shared lap's day, or yesterday's winner's). */
  private goToday(): Promise<void> {
    const u = new URL(location.href);
    if (u.searchParams.has('lap')) {
      u.searchParams.delete('lap');
      history.replaceState(null, '', u);
    }
    return this.load(romeDateString(), 0);
  }

  /**
   * Share your best lap: a link that plays it (one link per lap) and a few
   * lines to go with it. The text goes out in this same tap (phones only allow
   * the clipboard and the share sheet then); the lap uploads alongside.
   */
  private shareLap(how: 'copy' | 'send', from: 'poster' | 'card' | 'board' | null = null, say?: (label: string) => void): void {
    const best = this.session.day.best;
    // A link puts its lap on the board too: never a lap the bot drove.
    if (!best || this.eraOverride || this.autopilot) return;
    // Challenging them back: done.
    if (this.challengeBack !== null) {
      this.challengeBack = null;
      this.updatePoster();
    }
    // A link points at your lap on today's board, which needs your name: ask for it first.
    if (!this.player.name) {
      this.shareAfterName = true;
      this.boardEditing = true;
      this.boardMessage = 'Add your name, then challenge your friends';
      this.renderBoard();
      this.screens.openBoard();
      this.screens.board.focusName();
      return;
    }
    this.stats.count('shares');
    if (from) this.stats.count(from === 'poster' ? 'sharesPoster' : 'sharesBoard');
    writeLocal('gp.sentLink', romeDateString());
    if (this.challenge?.beatBy != null) this.step('sentBack');
    // One link a day: it follows your best lap, so a better one (or a new picture) just goes up again.
    const day = `${best.date}|${best.trackHash}|${best.physicsHash}`;
    if (!this.shared || this.shared.day !== day) this.shared = { day, id: this.ownLinkId(true), sent: null };
    const link = this.shared;
    const lapKey = this.shareKey(best);
    const you = this.board?.you ?? null;
    const onBoard = you !== null && you.lapMs === best.lapMs;
    const ch = this.challenge;
    const text = shareText({
      date: best.date,
      circuit: this.circuit.name,
      lapMs: best.lapMs,
      medals: this.circuit.medals,
      rank: onBoard ? you.rank : null,
      total: onBoard ? (this.board?.total ?? null) : null,
      url: shareUrl(link.id),
      beat: ch && ch.beatBy !== null ? { name: ch.name, byMs: ch.beatBy } : null,
    });
    // Inside Discord: Discord's own share window, with an Activity link that opens the game on this lap.
    const viaDiscord = this.discord ? shareChallenge(this.discord, `🏁 ${shortLap(best.lapMs)} at ${this.circuit.name} · can you beat it?`, link.id) : null;
    const sending = !viaDiscord && how === 'send' && canSend() ? sendText(text) : null;
    const copying = viaDiscord || sending ? null : copyText(text);
    // The lap goes up with its preview picture (usually drawn already; if not, it's worth the wait:
    // the chat app reads the preview as soon as the link is pasted).
    const upload: Promise<{ ok: boolean; reason?: string }> =
      link.sent === lapKey
        ? Promise.resolve({ ok: true })
        : this.shareCard(best).then((image) =>
            this.api.createShare({
              id: link.id,
              playerId: this.player.id,
              name: this.player.name,
              era: this.circuit.era,
              record: best,
              image: image ?? undefined,
              via: this.viaFor(best.date),
            }),
          );
    void (async () => {
      const up = await upload;
      if (up.ok) {
        // Sharing put your lap on the board: show your place.
        if (link.sent !== lapKey) {
          this.stats.markSaved();
          void this.refreshBoard();
        }
        link.sent = lapKey;
      } else {
        // A taken id (vanishingly rare) gets a new link next time; anything else retries this one.
        if (up.reason === 'id taken' && this.shared === link) {
          this.shared = null;
          writeLocal('gp.link', '');
        }
        const refused = up.reason === NO_REPLAY || up.reason === TOO_MANY_REFUSED ? up.reason : null;
        if (refused) {
          this.markRefused(best, refused);
          this.renderBoard();
        }
        this.screens.shareFeedback(
          up.reason === 'offline'
            ? 'offline · the link won’t work yet, try again'
            : up.reason === 'slow down'
              ? 'too many links at once · try again in a minute'
              : refused
                ? 'not saved · that lap didn’t replay exactly'
                : 'couldn’t save the link · try again',
        );
      }
    })();
    void (async () => {
      if (viaDiscord) {
        const ok = await viaDiscord;
        if (ok) this.screens.shareFeedback('sent ✓ · now it’s their move');
        say?.(ok ? 'sent ✓' : 'send it back ↗');
        if (ok) this.afterSent(true, best.lapMs);
        return;
      }
      const [copied, sent] = await Promise.all([copying, sending]);
      if (copying) this.screens.shareFeedback(copied ? 'link copied ✓ · paste it to a friend' : 'couldn’t copy · try again');
      else if (sent === 'failed') this.screens.shareFeedback('couldn’t open sharing · try copy');
      say?.(copying ? (copied ? 'link copied ✓' : 'couldn’t copy') : sent === 'sent' ? 'sent ✓' : sent === 'failed' ? 'couldn’t share' : 'send it back ↗');
      if (copying ? copied : sent === 'sent') this.afterSent(!copying, best.lapMs);
    })();
  }

  private seenHow(): boolean {
    if (readLocal('gp.how') === '1') return true;
    writeLocal('gp.how', '1');
    return false;
  }

  private go(): void {
    if (this.mode !== 'title' && this.mode !== 'paused') return;
    this.screens.closeAskName();
    const asked = this.lessonAsked;
    this.lessonAsked = null;
    // An old link's race is over: any way into racing leads to today's circuit instead.
    if (this.challenge?.expired) {
      this.leaveForToday();
      return;
    }
    void this.audio.start();
    this.audio.setCarSounds(true);
    (document.activeElement as HTMLElement | null)?.blur?.();
    // The first race on this browser offers the driving lesson, right as the race is about to start.
    const offer = !asked && this.mode === 'title' && this.lessonDue();
    // A new player's first steps: race pressed, and past the lesson's offer when there isn't one.
    this.stats.reach('race');
    if (!offer) this.stats.reach('answered');
    const fresh = this.mode === 'title' || asked !== null;
    if (!asked && !offer) this.step('linkRaced');
    if (fresh) this.restart();
    if (fresh && !asked && !offer) this.countRace();
    this.mode = 'running';
    if (!(fresh && !asked && !offer && this.tellCarNote())) this.suggestSideways();
    this.screens.hide();
    this.hud.setTitleMode(false);
    document.body.classList.remove('title-mode');
    this.last = performance.now();
    if (asked || offer) this.startLesson(offer);
  }

  /** A newcomer who keeps restarting without finishing a lap: the poster suggests the lesson (never to regulars). */
  private lessonNudge(): boolean {
    return this.restartsWithoutLap >= 3 && this.lifetimeLaps < POLL_AFTER_LAPS && readLocal('gp.lesson') !== 'done' && !everWonMedal();
  }

  /** A controller's Y (or R) on the poster: the lesson, when the poster offers it (its ticket, the how-to side). */
  private lessonFromPoster(): void {
    const view = this.screens.currentView;
    if (view === 'how' || (view === 'main' && this.lessonTicket() !== null)) this.askLesson('poster');
  }

  /** Offer the lesson on this race: a first race on this browser (not a preview), or ?lesson=1. */
  private lessonDue(): boolean {
    if (this.eraOverride || this.shotAt >= 0 || this.autopilot) return false;
    if (this.lessonForced) return true;
    // Offered once a visit at most (without storage, gp.lesson is never remembered).
    return !this.lessonOffered && readLocal('gp.lesson') === null && this.lifetimeLaps === 0;
  }

  /** The lesson asked for from the poster (a link, the how-to side, the nudge): straight in, no offer. */
  private askLesson(entry: LessonEntry): void {
    if (this.mode !== 'title' && this.mode !== 'paused') return;
    this.lessonAsked = entry;
    this.go();
  }

  private startLesson(offer: boolean): void {
    this.lessonForced = false;
    // The lesson teaches the two-thumb controls.
    this.touch.setLesson(true);
    if (offer) this.lessonOffered = true;
    const ui = this.lessonUi;
    const host = {
      session: () => this.session,
      runtime: () => this.circuit.runtime,
      track: () => this.circuit.track,
      plan: () => this.lessonPlan,
      top: () => this.lessonTop,
      events: () => this.lessonEvents,
      turn: () => this.turnOne,
      turnIndex: () => this.turnOneIndex,
      spawnIndex: () => this.spawnIndex,
      controls: () => this.controls(),
      ui,
      touch: this.touch,
      audio: this.audio,
      stats: this.stats,
      overlay: () => this.world.lesson,
      restart: () => this.restart(),
      finish: (next: 'race' | 'how') => this.finishLesson(next),
      remember: (v: 'taken' | 'skipped' | 'done') => writeLocal('gp.lesson', v),
    };
    this.lesson = new LessonDirector(host, offer, this.challenge && !this.challenge.expired ? this.challenge.name || 'your friend' : null);
  }

  /** The lesson is over (done, left or skipped): race for real, or turn to how to play. */
  private finishLesson(next: 'race' | 'how'): void {
    this.lesson = null;
    this.showLine();
    if (next === 'how') {
      this.touch.setLesson(false);
      this.restart();
      this.mode = 'title';
      this.showTitle(false);
      this.screens.show('how');
      return;
    }
    this.touch.setLesson(false);
    this.restart();
    this.countRace();
    // Racing a friend's lap after the lesson (or skipping it) is still racing their lap.
    this.step('linkRaced');
    this.audio.setCarSounds(true);
    this.mode = 'running';
    this.last = performance.now();
  }

  /** A new day loads: any lesson in progress is simply dropped. */
  private dropLesson(): void {
    if (!this.lesson) return;
    this.lesson = null;
    const ui = this.lessonUi;
    ui.hideCard();
    ui.showDemo(false);
    ui.setCue(null);
    ui.setBar(null);
    ui.setActive(false, this.controls());
    this.touch.cue(null);
    this.touch.setLesson(false);
  }

  /** A race starts (restarts too, not the lesson): counted, and the pedal slider's first races show how it works. */
  private countRace(): void {
    this.stats.count('raceStarts');
    if (!this.touch.active || !this.touch.sliding) return;
    this.stats.count('sliderRaces');
    const shown = Number(readLocal(DEMO_KEY) ?? 0);
    if (shown >= DEMO_RACES) return;
    writeLocal(DEMO_KEY, String(shown + 1));
    this.touch.showDemo(true);
    this.stats.count('sliderDemo');
  }

  /**
   * The driving line and braking zones on the road in normal races: off unless switched on from the poster
   * (the lesson shows them while it runs, and they go away once it's skipped, left or done).
   */
  /** Screen shake: on unless switched off on the pause poster. */
  private shakeOn(): boolean {
    return readLocal(SHAKE_KEY) !== 'off';
  }

  private lineOn(): boolean {
    return readLocal('gp.line') === 'on';
  }

  private showLine(): void {
    const overlay = this.world?.lesson;
    if (!overlay || this.lesson) return;
    const on = this.lineOn() && !this.eraOverride && this.shotAt < 0;
    overlay.setLine(on);
    overlay.showEvents(on ? 'all' : 'none');
  }

  /**
   * A car rebuilt today (CAR_NOTES): what changed, once, to players who've raced before, over the car before it
   * moves; the car waits for "got it". ?note=1 shows it anyway (the maker's own copies).
   */
  private tellCarNote(): boolean {
    const n = carNote(this.circuit.date, this.circuit.era);
    if (!n || this.eraOverride || this.shotAt >= 0 || this.autopilot) return false;
    if (!(DEV_HOST && params.get('note') === '1')) {
      if (this.lifetimeLaps === 0 || readLocal(`gp.carNote.${n.id}`)) return false;
      writeLocal(`gp.carNote.${n.id}`, '1');
    }
    this.noteHold = true;
    // The touch controls step back, or the steering's strip takes the tap meant for "got it".
    document.body.classList.add('car-note');
    // Enter is "got it" on a keyboard (the driving keys aren't, so nobody sets off without reading it).
    const onKey = (e: KeyboardEvent): void => {
      if (e.code === 'Enter' || e.code === 'NumpadEnter') this.hud.closeNote();
    };
    window.addEventListener('keydown', onKey);
    this.hud.showNote(n, () => {
      this.noteHold = false;
      document.body.classList.remove('car-note');
      window.removeEventListener('keydown', onKey);
      this.last = performance.now();
    });
    return true;
  }

  /** The first race on an upright phone: suggest turning it (once per device). */
  private suggestSideways(): void {
    if (!isTouchDevice() || window.innerHeight <= window.innerWidth) return;
    try {
      if (localStorage.getItem('gp.sideways') === '1') return;
      localStorage.setItem('gp.sideways', '1');
    } catch {
      return;
    }
    this.hud.showBanner('turn your phone sideways', 'for a wider view of the track', '', 4.5);
  }

  /** How far round the lap the car is (0 to 1; 1 on the run-up to the line, before the lap has started). */
  private lapProgress(): number {
    const st = this.session.state;
    return st.timing.started ? st.car.s / this.circuit.runtime.length : 1;
  }

  /** Stuck or crashed (game/rescue.ts): watch the car while racing; anywhere else, nothing up. */
  private watchRescue(dt: number): void {
    const car = this.session.state.car;
    const t = pointAt(this.circuit.runtime, car.s);
    // The wrong way round: more than about 100 degrees off the track's direction.
    const facingBack = Math.cos(car.heading) * t.tx + Math.sin(car.heading) * t.ty < -0.17 && Math.hypot(car.vx, car.vy) < 30;
    if (this.rescue.frame(dt, car, this.lastRaw.throttle, this.lapProgress(), facingBack, this.lesson !== null)) this.showRescue();
  }

  /**
   * What the rescue says, where the player looks: on a phone, its buttons (BRAKE or the pedal slider, and ↺ by
   * the clock); with keys or a controller, the key to press at the foot of the screen.
   */
  private showRescue(): void {
    const s = this.rescue.showing;
    const way = this.controls();
    this.touch.backOut(way === 'touch' ? s.backOut : null);
    this.touch.restartHint(way === 'touch' ? s.restart : null);
    if (way === 'touch') {
      this.hud.setRescue([]);
      return;
    }
    const pad = way === 'pad';
    const rows: RescueRow[] = [];
    if (s.backOut) rows.push({ keys: pad ? ['LT'] : ['↓', 'S'], words: s.backOut === 'words' ? 'hold to back out' : null });
    if (s.restart) rows.push({ keys: pad ? ['Y'] : ['R'], words: s.restart === 'words' ? 'restart for a fresh lap' : null });
    this.hud.setRescue(rows);
  }

  private pause(): void {
    this.mode = 'paused';
    this.showTitle(true);
    this.askForName();
  }

  /** The name the game gave this player (their first clean lap went up under it), while it's still theirs. */
  private get madeUpName(): string | null {
    const m = readLocal('gp.madeUp');
    return m && m === this.player.name ? m : null;
  }

  /**
   * The first pause after a lap went up under a made-up name: a card over the pause menu asking for their
   * own (once; after that the chip in the corner is the way).
   */
  private askForName(): void {
    const name = this.madeUpName;
    const you = this.board?.you ?? null;
    if (!name || !you || this.boardStatus !== 'ready' || readLocal('gp.madeUpAsked')) return;
    writeLocal('gp.madeUpAsked', '1');
    this.screens.askName(
      { name, line: `P${you.rank} of ${this.board?.total ?? you.rank} · ${formatLap(you.lapMs)}`, focus: !isTouchDevice() },
      (typed: string) => void this.saveToBoard(typed),
      () => this.keepMadeUpName(),
    );
  }

  private keepMadeUpName(): void {
    this.screens.closeAskName();
  }

  private restart(): void {
    this.session.restart();
    this.rescue.raceStart();
    this.showRescue();
    this.world.chase.snap();
    this.acc = 0;
    this.hud.setSectors([null, null, null]);
    this.pastTurnOne = false;
  }

  private onSession(e: Parameters<Parameters<Session['on']>[0]>[0]): void {
    switch (e.type) {
      case 'restart':
        this.liveRun++;
        break;
      case 'lapStart':
        this.hud.setSectors([null, null, null]);
        this.excitement = 1;
        break;
      case 'sector':
        this.hud.setSectors(this.session.lapSectors);
        break;
      case 'invalidated':
        this.hud.showBanner('Lap invalid', 'track limits · all four wheels off', 'bad', 2.6);
        if (!this.lesson || this.lesson.practising) {
          this.rescue.invalid(this.lapProgress());
          this.showRescue();
        }
        break;
      case 'barrier':
        this.audio.thud(e.speed);
        if (!this.lesson || this.lesson.practising) {
          this.rescue.hit(e.speed, this.lapProgress());
          this.showRescue();
        }
        break;
      case 'lap': {
        const r = e.result;
        // The lesson's lap is practice: nothing is counted, saved or celebrated; the lesson just ends.
        if (r.practice) {
          this.lesson?.onLap(r);
          break;
        }
        this.restartsWithoutLap = 0;
        if (r.medals.length) writeLocal('gp.medal', '1');
        this.stats.count('laps');
        if (r.valid && !r.tuned) this.stats.count('validLaps');
        if (r.isPB) this.stats.count('bests');
        writeLocal('gp.laps', String(++this.lifetimeLaps));
        this.hud.showLap(r, this.session.lapSectors);
        const ch = this.challenge;
        const beatsChallenge = !!ch && r.valid && !r.tuned && r.ms < ch.lapMs && ch.beatBy === null;
        // The day's first clean lap on today's board: the stamp, confetti and the crowd (a beaten challenge
        // has its own banner, the bigger news).
        const first = r.isPB && r.previousBestMs === null && !r.tuned && !this.boardBlocked() && !beatsChallenge;
        const topMedal = r.medals.length ? `${this.hud.medalName(r.medals[r.medals.length - 1])} medal` : null;
        if (r.tuned) this.hud.showBanner('Tuning edited', 'lap not saved (dev physics)', 'bad', 2.4);
        else if (first) {
          this.hud.celebrate(formatLap(r.ms), topMedal, isTouchDevice() ? 110 : 200);
          this.audio.cheer(3.6);
        } else if (r.isPB) {
          this.hud.showBanner('New best', r.previousBestMs === null ? 'your first clean lap today' : `personal best · ${((r.ms - r.previousBestMs) / 1000).toFixed(3)} s`, 'good', 3);
          this.audio.cheer(3);
        }
        if (topMedal && !first) {
          this.hud.showBanner(topMedal, 'target beaten', 'gold', 3.2);
          this.audio.cheer(3);
        }
        if (ch && beatsChallenge) {
          this.audio.cheer(3);
          if (!ch.battle) this.step('linkBeat');
          else if (!ch.counted.has('battleWon')) {
            ch.counted.add('battleWon');
            this.stats.count('battleWon');
          }
        }
        this.hud.setMedals(this.circuit.medals, this.session.day.medals);
        // A new best is kept (saved, on the board) once its check is done, a moment later: see 'best'.
        break;
      }
      case 'best': {
        // The new best replayed exactly: it's the day's best now (and a good moment to send it: the nudge).
        this.goodLap = true;
        const unbeaten = this.challenge !== null && this.challenge.beatBy === null;
        this.checkBeaten();
        // It beat the challenge: say so, with the reply one tap (or C) away.
        const won = this.challenge;
        if (unbeaten && won && won.beatBy !== null && !won.expired) {
          const who = won.name || 'your friend';
          this.hud.showBanner(`You beat ${won.name || 'the lap'}`, `by ${shortLap(won.beatBy)} s · send it and it’s ${who}’s move`, 'gold', 6, {
            label: this.controls() === 'touch' ? 'send it back ↗' : 'send it back ↗ · C',
            onTap: (say) => this.sendBack(say),
          });
        }
        this.hud.setBest(this.session.day.best?.lapMs ?? null);
        this.dev.refresh();
        if (this.player.name) void this.submitBest();
        else if (!this.boardBlocked()) {
          // No name yet: the lap goes up anyway, under one the game makes up (theirs to change any time).
          const name = madeUpName(this.player.id);
          this.player = { ...this.player, name };
          savePlayer(this.player);
          writeLocal('gp.madeUp', name);
          this.stats.count('madeUpName');
          void this.submitBest();
        } else this.renderBoard();
        break;
      }
      case 'bestRejected':
        this.hud.showBanner('Lap not saved', 'it didn’t replay exactly · sorry', 'bad', 3);
        this.dev.refresh();
        break;
    }
  }

  /** Why laps from this session can't go on the board (a preview, an old or future day), or null. */
  private boardBlocked(): BoardStatus | null {
    if (this.eraOverride) return 'preview';
    const today = romeDateString();
    if (this.circuit.date < today) return 'closed';
    if (this.circuit.date > today) return 'future';
    return null;
  }

  private async refreshBoard(): Promise<void> {
    const circuit = this.circuit;
    if (this.boardBlocked() === 'preview') {
      this.board = null;
      this.boardStatus = 'preview';
      this.renderBoard();
      return;
    }
    this.boardFetchedAt = performance.now();
    const b = await this.api.getLeaderboard(circuit.date, circuit.trackHash, this.session.basePhysicsHash, this.player.id, this.boardGuild());
    if (this.circuit !== circuit) return; // another day was loaded meanwhile
    // A new target unlocked (you moved up): say so, while racing.
    const target = b?.target ?? null;
    if (target?.ref && this.lastTarget !== undefined && target.ref !== this.lastTarget && this.mode === 'running') {
      this.hud.showBanner(`${target.name}’s lap unlocked`, 'study your next target on the leaderboard', 'gold', 3.2);
    }
    if (b) this.lastTarget = target?.ref ?? null;
    this.board = b;
    this.boardStatus = !b ? 'offline' : b.closed ? 'closed' : (this.boardBlocked() ?? 'ready');
    this.renderBoard();
    this.battleNews(b?.battles);
    this.maybeNudge();
    // The poster's line under the medals and today's card (and a challenge's programme) follow the board.
    this.updatePoster();
    // A best lap that never made it up (the server was unreachable): send it now.
    const best = this.session.day.best;
    if (b && this.boardStatus === 'ready' && this.player.name && best && !this.boardBusy && !this.refusedLap(best) && (!b.you || b.you.lapMs > best.lapMs)) {
      void this.submitBest();
    }
    void this.fetchBoardOwn();
  }

  private renderBoard(): void {
    if (!this.session) return;
    // The HUD's place and save prompt always; the board itself (on the poster) only when it can be seen:
    // drawing it behind a race cost a hitch (showTitle draws it when the poster comes back).
    if (this.screens.visible) this.drawBoard();
    const you = this.board?.you ?? null;
    const bestMs = this.session.day.best?.lapMs ?? null;
    this.hud.setRank(you && this.boardStatus === 'ready' ? `P${you.rank} of ${this.board?.total ?? 0}` : null);
    // A first best lap that isn't on the board yet: nudge (later bests go up on their own). On it under a
    // made-up name: the chip says so, to rename.
    const madeUp = this.madeUpName;
    this.hud.setSavePrompt(this.boardStatus === 'ready' && bestMs !== null && ((!this.player.name && !you) || (!!madeUp && !!you)), madeUp);
  }

  private drawBoard(): void {
    const car = ERAS[this.circuit.era];
    const ms = msUntilRomeMidnight();
    const left = `closes in ${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
    const when = this.boardStatus === 'closed' ? 'closed' : this.boardStatus === 'preview' ? 'preview' : this.boardStatus === 'future' ? 'not open yet' : left;
    const bestMs = this.session.day.best?.lapMs ?? null;
    this.screens.board.render({
      status: this.boardStatus,
      board: this.board,
      circuit: this.circuit.name,
      sub: `${car.name.toLowerCase()} ${car.year} · ${when}`,
      bestMs,
      name: this.player.name,
      busy: this.boardBusy,
      editing: this.boardEditing,
      message: this.boardMessage,
      watchable: this.boardStatus === 'ready' || this.boardStatus === 'closed',
      ladder: this.ladderLine(),
      challenge: !!this.board?.you && this.canChallenge(),
      scope: this.scopeInfo(),
      signIn: this.signInInfo(),
    });
  }

  /** Inside a Discord server (today's board): the switch between the server's board and everyone's. */
  private scopeInfo(): BoardScope | null {
    const g = this.discord?.guild;
    if (!g || this.circuit.date !== romeDateString() || this.eraOverride) return null;
    return { server: g.name, on: this.boardScope, ...(this.lapPoster ? { posts: this.lapPoster.enabled } : {}), ...(this.live ? { live: this.liveShown } : {}) };
  }

  /** The switch: the server's board or everyone's, fetched afresh (the poster and the leaderboard follow). */
  private setBoardScope(on: BoardScope['on']): void {
    if (on === this.boardScope) return;
    this.boardScope = on;
    this.board = null;
    this.boardStatus = 'loading';
    this.lastTarget = undefined;
    this.renderBoard();
    this.updatePoster();
    void this.refreshBoard();
  }

  /** Where you stand on the replay ladder: who you can watch next, or why nobody. */
  private ladderLine(): string | null {
    const b = this.board;
    const you = b?.you ?? null;
    if (!b || !you || this.boardStatus !== 'ready') return null;
    const behind = you.rank < b.total ? ' · everyone behind you is open too' : '';
    if (b.target) return `▸ ${b.target.name}’s lap is unlocked: your next target${behind}`;
    if (you.rank === 1) return `You’re leading: your lap stays secret till tomorrow${behind}`;
    if (you.rank === 2) return `Only the leader is ahead, and their lap stays secret till tomorrow${behind}`;
    return null;
  }

  private async saveToBoard(raw: string): Promise<void> {
    const name = cleanName(raw);
    if (!name) {
      this.boardMessage = 'Type your name first';
      this.renderBoard();
      return;
    }
    if (isProfane(name)) {
      this.boardMessage = "That name isn't allowed · pick another one";
      this.renderBoard();
      return;
    }
    // Their own name now (even if they kept the made-up one's spelling: they chose it).
    if (readLocal('gp.madeUp')) {
      removeLocal('gp.madeUp');
      this.stats.count('madeUpRenamed');
    }
    this.player = { ...this.player, name };
    savePlayer(this.player);
    this.boardEditing = false;
    this.screens.closeAskName();
    await this.submitBest();
    // Asked for a name to share: saved, so back to the share links (the copy itself needs a fresh tap).
    if (this.shareAfterName && this.board?.you) {
      this.shareAfterName = false;
      this.screens.show('main');
      this.screens.shareFeedback('saved · now challenge your friends ↗\uFE0E');
    }
  }

  /** Put today's best lap on the board under the player's name. */
  private async submitBest(): Promise<void> {
    const record = this.session.day.best;
    // A lap the bot drove never goes on a board.
    if (!record || !this.player.name || this.boardBlocked() || this.autopilot) return;
    const refused = this.refusedLap(record);
    if (refused) {
      this.boardMessage = `Not saved · ${refused}`;
      this.renderBoard();
      return;
    }
    const circuit = this.circuit;
    this.boardBusy = true;
    this.boardMessage = null;
    this.renderBoard();
    // Inside a Discord server: the save also says to post it there when the player leaves (the server draws it).
    const discord = record.date === romeDateString() ? this.lapPoster?.post() : undefined;
    const res = await this.api.submitLap({
      playerId: this.player.id,
      name: this.player.name,
      era: circuit.era,
      record,
      via: this.viaFor(record.date),
      ...(discord ? { discord } : {}),
      ...(IN_DISCORD ? { inDiscord: true } : {}),
    });
    if (this.circuit !== circuit) return;
    this.boardBusy = false;
    if (!res.accepted) {
      this.boardMessage =
        res.reason === 'offline'
          ? 'Offline · your lap goes up when the server is back'
          : res.reason === 'closed'
            ? 'Leaderboard closed · this day is over'
            : res.reason === 'name not allowed'
              ? "That name isn't allowed · pick another one"
              : res.reason === 'slow down'
                ? 'Too many saves at once · try again in a minute'
                : `Not saved · ${res.reason ?? 'server error'}`;
      // A name the board won't take: open the name form so it can be changed.
      if (res.reason === 'name not allowed') this.boardEditing = true;
      if (res.reason === NO_REPLAY || res.reason === TOO_MANY_REFUSED) this.markRefused(record, res.reason);
      this.renderBoard();
      return;
    }
    this.stats.markSaved();
    this.savedThisVisit = true;
    if (discord) this.lapPoster?.saved();
    await this.refreshBoard();
    // A first clean lap's stamp, if it's still up: where it ranks now that the board has it.
    const you = this.board?.you;
    if (you) this.hud.setCelebrationRank(`P${you.rank} of ${this.board?.total ?? you.rank} today`);
  }

  /** Normal play: count getting past turn 1 once per start (where newcomers most often give up). */
  private checkTurnOne(): void {
    if (this.pastTurnOne || !this.turnOne) return;
    const n = this.circuit.runtime.n;
    const prog = (i: number): number => (((i - this.spawnIndex) % n) + n) % n;
    const p = prog(trackPosition(this.session.state.car, this.circuit.runtime));
    if (p >= prog(this.turnOne.exitIndex) && p < n - 60) {
      this.pastTurnOne = true;
      this.stats.count('pastTurnOne');
    }
  }

  /** Normal play: how far round the lap the car is, in tenths (a new player's first day, for the stats). */
  private trackProgress(): void {
    const n = this.circuit.runtime.n;
    const p = (((trackPosition(this.session.state.car, this.circuit.runtime) - this.spawnIndex) % n) + n) % n;
    // Just behind the line on the grid reads as the end of the lap: not progress.
    if (p >= n - 60) return;
    this.stats.progress(Math.min(9, Math.floor((10 * p) / n)));
  }

  /** Road height at arc length s, and the nose-up angle for a car heading that way. */
  private road(s: number, heading: number): { h: number; pitch: number; bank: number } {
    const t = this.circuit.track;
    const n = t.n;
    const L = t.length;
    let u = s % L;
    if (u < 0) u += L;
    // Samples are nearly evenly spaced: start from the estimate, walk to the right segment.
    let i = Math.min(n - 1, Math.floor((u / L) * n));
    while (i > 0 && t.s[i] > u) i--;
    while (i < n - 1 && t.s[i + 1] <= u) i++;
    const j = (i + 1) % n;
    const seg = (j === 0 ? L : t.s[j]) - t.s[i] || 1;
    const h = t.elev[i] + ((t.elev[j] - t.elev[i]) * (u - t.s[i])) / seg;
    const grade = (t.elev[j] - t.elev[i]) / seg;
    const along = t.tx[i] * Math.cos(heading) + t.ty[i] * Math.sin(heading);
    const bank = t.bank ? t.bank[i] + ((t.bank[j] - t.bank[i]) * (u - t.s[i])) / seg : 0;
    return { h, pitch: Math.atan(grade * along), bank };
  }

  /** Height a banked road adds at lateral offset d (the tilt stops at the road's edges). */
  private tilt(bank: number, d: number): number {
    if (bank === 0) return 0;
    const edge = this.circuit.track.halfWidth + this.circuit.track.curbWidth;
    return (d < -edge ? -edge : d > edge ? edge : d) * bank;
  }

  /** Signed lateral offset (left positive) of a point from the centreline at arc length s. */
  private lateral(s: number, x: number, y: number): number {
    const p = pointAt(this.circuit.runtime, s);
    return (x - p.x) * -p.ty + (y - p.y) * p.tx;
  }

  /**
   * Watching a podium: a flag over each ghost with its name and its gap to the car being followed
   * ("+0.31": that far behind at this spot); a ghost out of sight gets its flag on the screen's edge.
   */
  private groupTags(focus: Spectator, others: { sp: Spectator; i: number }[], ghosts: CarVisual[], renderTick: number): void {
    const pw = this.group;
    if (!pw) return;
    const t = focus.finished ? null : focus.lapElapsedMs(renderTick);
    this.placeFlags(
      others.map((r) => {
        const l = pw.laps[r.i];
        const track = pw.tracks[r.i];
        // Their time to this spot, against the followed car's time here.
        const d = track && t !== null && t > 300 ? deltaAt(track, focus.state.car.s, t) : null;
        const gap = d === null ? null : `${d > 0 ? '−' : '+'}${(Math.abs(d) / 1000).toFixed(2)}`;
        return { place: pw.podium && !l.own ? l.place : null, name: l.own ? 'you' : l.name || 'your friend', gap, own: !!l.own, gold: !!l.gold };
      }),
      ghosts,
    );
  }

  /**
   * Racing with friends on track: the few near yours along the track (each one drawn is a whole car). Those further
   * round can't be seen anyway: no flag pinned to the edge for them, only for someone close ahead or behind.
   */
  private nearestFriends(cars: LiveCar[], most = 3, within = 250): LiveCar[] {
    const L = this.circuit.track.length;
    const mine = this.session.state.car.s;
    const apart = (c: LiveCar): number => {
      const d = Math.abs(c.sp.state.car.s - mine) % L;
      return Math.min(d, L - d);
    };
    return cars
      .filter((c) => apart(c) <= within)
      .sort((a, b) => apart(a) - apart(b))
      .slice(0, most);
  }

  /** Watching live: a flag over each of the other drivers' cars (or at the edge it's beyond), with their name. */
  private liveFlags(cars: LiveCar[], ghosts: CarVisual[]): void {
    this.placeFlags(
      cars.map((c) => ({ place: null, name: c.paused ? `${c.name} · paused` : c.name, gap: null, own: false, gold: c.gold, live: true })),
      ghosts,
    );
  }

  /** The flags over the ghosts (`ghosts[k]` is flag k's car), on screen or pinned to the edge each car is beyond. */
  private placeFlags(bases: { place: number | null; name: string; gap: string | null; own: boolean; gold: boolean; live?: boolean }[], ghosts: CarVisual[]): void {
    const { w, h } = this.view;
    const edge = { behind: 0, left: 0, right: 0 };
    const tags = bases.map((base, k) => {
      const v = ghosts[k];
      const at = this.world.screenPoint(v.x, v.y + 1.9, v.z, w, h);
      // In view: over the car (even while it's faded, right beside the one followed).
      // Kept whole on screen (a flag is about 120 px wide).
      if (at) return { ...base, x: Math.min(w - 64, Math.max(64, at.x)), y: Math.max(70, at.y), alpha: Math.max(0.75, this.world.extraOpacity[k] ?? 0), pinned: null };
      const cs = this.world.cameraSpace(v.x, v.y, v.z);
      if (cs.z > -2) {
        // Behind the camera: along the foot of the screen, above the way out.
        const x = w / 2 + Math.max(-1, Math.min(1, cs.x / 12)) * w * 0.28;
        return { ...base, x, y: h - Math.min(150, h * 0.2) - edge.behind++ * 30, alpha: 1, pinned: 'behind' as const };
      }
      const side = cs.x > 0 ? ('right' as const) : ('left' as const);
      return { ...base, x: side === 'right' ? w - 70 : 70, y: h * 0.56 + edge[side]++ * 30, alpha: 1, pinned: side };
    });
    // Two flags in the same spot (cars side by side): the later one goes up a line.
    for (let a = 0; a < tags.length; a++) {
      for (let b = 0; b < a; b++) {
        if (Math.abs(tags[a].x - tags[b].x) < 110 && Math.abs(tags[a].y - tags[b].y) < 26) tags[a].y = tags[b].y - 28;
      }
    }
    this.hud.setGhostTags(tags);
  }

  /**
   * Your lap against the one you're watching, at the spot the followed car is at ("+0.31": you were that far
   * behind here), or null when your ghost isn't out (or the lap's barely begun, or it's over).
   */
  private versusGap(focus: Spectator, renderTick: number): number | null {
    const pw = this.group;
    const o = pw ? pw.laps.findIndex((l) => l.own) : -1;
    if (!pw || o < 0 || this.ownGhostOff || focus.finished) return null;
    const t = focus.lapElapsedMs(renderTick);
    if (t === null || t <= 300) return null;
    // Following yours: against theirs (the podium's: the first place's); following theirs: yours against it.
    if (pw.focus === o) {
      const theirs = pw.tracks[pw.laps.findIndex((l) => !l.own)];
      return theirs ? deltaAt(theirs, focus.state.car.s, t) : null;
    }
    const d = pw.tracks[o] ? deltaAt(pw.tracks[o], focus.state.car.s, t) : null;
    return d === null ? null : -d;
  }

  /** At the line: your lap's time and each sector against theirs, and where it lost (or gained) the most. */
  private versusSplit(): { line: string; split: number[]; note: string } | null {
    const pw = this.group;
    const mine = pw?.laps.find((l) => l.own);
    const theirs = pw?.laps[pw.focus].own ? pw.laps.find((l) => !l.own) : pw?.laps[pw.focus];
    if (!pw || !mine || !theirs || this.ownGhostOff) return null;
    const line = pw.laps[pw.focus].own ? `${theirs.name ? `${theirs.name}’s` : 'theirs'} was ${shortLap(theirs.lapMs)}` : `yours was ${shortLap(mine.lapMs)}`;
    return { line, ...sectorSplit(mine.record, theirs.record) };
  }

  /** The length of the lap being watched (for its progress bar). */
  private watchedLapMs(): number {
    const pw = this.group;
    if (pw) return pw.laps[pw.focus].lapMs;
    return this.boardView?.lapMs ?? this.challenge?.lapMs ?? 0;
  }

  /** Where a replayed car is on screen this frame: its pose between ticks, on the road's height and banking. */
  private carVisual(sp: Spectator, alpha: number): CarVisual {
    const c = sp.state.car;
    const x = sp.prevX + (c.x - sp.prevX) * alpha;
    const y = sp.prevY + (c.y - sp.prevY) * alpha;
    let dh = c.heading - sp.prevHeading;
    if (dh > Math.PI) dh -= Math.PI * 2;
    else if (dh < -Math.PI) dh += Math.PI * 2;
    const heading = sp.prevHeading + dh * alpha;
    const L = this.circuit.track.length;
    let dS = c.s - sp.prevS;
    if (dS < -L / 2) dS += L;
    else if (dS > L / 2) dS -= L;
    const sNow = sp.prevS + dS * alpha;
    const road = this.road(sNow, heading);
    const d = road.bank !== 0 ? this.lateral(sNow, x, y) : 0;
    const sf = c.surfaces;
    const curbMask = (sf[0] === 1 ? 1 : 0) | (sf[1] === 1 ? 2 : 0) | (sf[2] === 1 ? 4 : 0) | (sf[3] === 1 ? 8 : 0);
    return {
      x,
      z: -y,
      y: road.h + this.tilt(road.bank, d),
      pitch: road.pitch,
      roll: Math.atan(road.bank),
      yaw: heading,
      wheelAngle: c.wheelAngle,
      speed: c.vx,
      ax: c.ax,
      ay: c.ay,
      curbMask,
      offWheels: sf.filter((v) => v === Surface.Grass || v === Surface.Gravel).length,
      impact: 0,
    };
  }

  /** The watched lap crosses the line: its time; a moment later, your turn. */
  private watchProgress(sp: Spectator): void {
    // Friends driving live never finish: they drive on until they stop.
    if (this.liveWatch) return;
    if (sp.finished && !this.watchBannerShown) {
      this.watchBannerShown = true;
      this.hud.watchFinished(shortLap(sp.finished.ms), this.whoseLap(), this.versusSplit());
      this.audio.cheer(2);
    }
    if (sp.done) this.endWatch();
  }

  /** The day's 3D world (again, for the light scene): drawn from the game's state every frame, so it can be swapped. */
  private buildWorld(): void {
    if (this.world) disposeWorld(this.world);
    this.world = new World({
      renderer: this.renderer,
      circuit: this.circuit,
      dateLabel: formatDate(this.circuit.date),
      lowQuality: this.light,
      lessonEvents: this.lessonEvents.map((e) => ({ brakeIndex: e.brakeIndex, minIndex: e.minIndex, dir: e.dir })),
      // The day's setting (track/setting.ts), unless ?setting= asks for one to try it.
      setting: SETTING_PARAM ?? settingForDate(this.circuit.date),
    });
    this.showLine();
    this.world.chase.motion = this.shakeOn();
    // Every material ready for the graphics chip now (behind the loading poster), not mid-race.
    this.world.precompile();
    for (const [n, on] of Object.entries(this.overlays)) this.world.dev[n as OverlayName].visible = on;
    this.world.postEnabled = this.dev.state.post;
    // Yesterday's winner's car is gold (again, on a world built afresh: the light scene).
    this.syncGold();
  }

  /**
   * For the stats: the sharpness this visit's device raced at (counted at its first race), and if it struggles.
   * A computer that drops frames even at the lowest sharpness gets the light scene phones get, at the next safe
   * moment (the poster or the pause menu, never mid-lap); only if that drops frames too is it counted as slow.
   */
  private countSharpness(): void {
    if (!this.sharpCounted && this.mode === 'running') {
      this.sharpCounted = true;
      const r = this.renderer.getPixelRatio();
      this.stats.count(r >= 1.99 ? 'resFull' : r >= 1.74 ? 'resHigh' : r >= 1.49 ? 'resMid' : r >= 0.99 ? 'resLow' : 'resSoft');
      this.countLight();
    }
    if (!this.sharp.struggling) return;
    if (!this.light) {
      if (this.mode === 'title' || this.mode === 'paused') this.goLight();
      return;
    }
    if (!this.slowCounted) {
      this.slowCounted = true;
      this.stats.count('resSlow');
    }
  }

  private goLight(): void {
    this.light = true;
    writeLocal(LIGHT_KEY, String(Date.now()));
    this.buildWorld();
    this.resize();
    this.world.chase.snap();
    this.sharp.retry(performance.now());
    this.countLight();
  }

  /** A computer on the light scene, once a visit (phones always are). */
  private countLight(): void {
    if (this.lightCounted || !this.light || isTouchDevice()) return;
    this.lightCounted = true;
    this.stats.count('resLight');
  }

  /** How to turn the graphics card back on, for this browser. */
  private gpuHelp(): void {
    // Inside Discord's app: Discord's own prompt to turn hardware acceleration on.
    if (this.discord) {
      void this.discord.sdk.commands.encourageHardwareAcceleration().catch(() => undefined);
      return;
    }
    const ua = navigator.userAgent;
    const [name, where, setting] = /Edg\//.test(ua)
      ? ['Edge', 'Settings › System and performance', 'Use graphics acceleration when available']
      : /Firefox\//.test(ua)
        ? ['Firefox', 'Settings › General › Performance', 'Use hardware acceleration when available']
        : /OPR\//.test(ua)
          ? ['Opera', 'Settings › Advanced › Browser › System', 'Use hardware acceleration when available']
          : /Chrome\//.test(ua)
            ? ['Chrome', 'Settings › System', 'Use graphics acceleration when available']
            : ['your browser', 'its settings (search for “hardware acceleration”)', 'hardware acceleration'];
    const steps =
      name === 'Firefox'
        ? [`Open ${where}`, 'Untick “Use recommended performance settings”', `Tick “${setting}”, then restart Firefox`]
        : [`Open ${where}`, `Turn on “${setting}”`, `Restart ${name}, then come back`];
    this.screens.showPopup({
      kicker: 'running slowly?',
      title: 'turn on hardware acceleration',
      line: 'Your browser is drawing the game without the graphics card, so it can’t run smoothly.',
      steps,
      go: 'got it',
      onGo: () => {},
      onKeep: () => {},
    });
  }

  /** The game's size on screen, as last measured. */
  private view = { w: window.innerWidth, h: window.innerHeight };

  private resize(): void {
    // The whole screen, also in an iPhone home-screen app (see ui/fullscreen.ts).
    this.view = gameSize();
    this.world?.resize(this.view.w, this.view.h);
    this.screens.resize();
  }

  /** The game hit an error it can't recover from: stop, and say so. */
  private dead = false;

  die(e: unknown): void {
    console.error(e);
    if (this.dead) return;
    this.dead = true;
    reportProblem('crash', e, this.mode);
    this.audio.setCarSounds(false);
    bootProblem('crash');
  }

  private frame(now: number): void {
    if (this.dead) return;
    requestAnimationFrame((t) => this.frame(t));
    try {
      this.tick(now);
    } catch (e) {
      this.die(e);
    }
  }

  private tick(now: number): void {
    // A new best being checked: a few milliseconds of it a frame, never a freeze at the line.
    this.session?.verifyStep(4);
    // How long this frame took, for the sharpness: judged on the poster and while racing, changed only on the poster.
    const moment: Moment = document.hidden || this.mode === 'loading' ? 'idle' : this.mode === 'title' || this.mode === 'paused' ? 'safe' : 'racing';
    this.sharp.frame(now - this.last, moment, now);
    this.countSharpness();
    const dt = Math.min(0.1, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    if (dt > 0) this.fps += (1 / dt - this.fps) * 0.05;
    if (this.mode === 'loading' || !this.world) return;

    const session = this.session;
    // While racing the controller is read with every sim step; the rest of the time (the poster, a
    // replay, a lesson card or freeze) it's read here, so its buttons (and the lesson's brake) work there too.
    if (this.mode !== 'running' || this.lesson?.frozen || this.noteHold) {
      const raw = this.input.sample();
      if (this.mode === 'running') {
        if (raw.throttle > 0) this.stats.reach('gas');
        this.lesson?.whileFrozen(raw.throttle, raw.brake);
      }
    }
    const lesson = this.mode === 'running' ? this.lesson : null;
    const watching = this.mode === 'watching' ? this.spectator : null;
    const running = (this.mode === 'running' && !lesson?.frozen && !this.noteHold) || watching !== null;
    // Friends driving live: a car far behind (just joined) skips ahead to just behind them.
    if (watching) this.liveWatch?.frame();
    else if (this.mode === 'running') this.together?.frame();
    if (running) {
      // The lesson's instructor drives at half speed.
      this.acc += dt * (lesson?.speedScale ?? 1);
      let steps = 0;
      while (this.acc >= SIM_DT && steps < 8) {
        if (this.shotAt >= 0 && this.simTime >= this.shotAt) {
          this.acc = 0;
          (window as unknown as { __shotReady: boolean }).__shotReady = true;
          break;
        }
        let stop = false;
        // A podium's laps all step together; friends driving live, each as their inputs arrive; otherwise the one lap being watched.
        if (watching && this.liveWatch) this.liveWatch.step();
        else if (watching) for (const sp of this.group?.runners ?? [watching]) sp.step();
        else {
          const raw = this.input.sample();
          this.lastRaw = raw;
          if (raw.throttle > 0) this.stats.reach('gas');
          let f = this.autopilot ? botInput(session.state.car, this.circuit.runtime, this.plan, session.physics) : quantizeInput(raw.throttle, raw.brake, raw.steer);
          if (this.lesson) f = this.lesson.frame(f);
          session.step(f);
          // Friends racing in the same Discord server: their cars tick with yours.
          this.together?.step();
          // The lesson looks after every step; a freeze or a card stops the sim right there.
          if (this.lesson) stop = this.lesson.afterStep(f);
          else {
            this.checkTurnOne();
            this.trackProgress();
          }
        }
        this.simTime += SIM_DT;
        this.acc -= SIM_DT;
        steps++;
        if (stop) {
          this.acc = Math.min(this.acc, SIM_DT);
          break;
        }
      }
      if (steps === 8) this.acc = 0;
    }
    // Time playing, for the stats: driving (the car moving, not sat on the grid) and watching laps.
    if (watching) this.stats.watching(dt * 1000);
    else if (this.mode === 'running' && (!lesson || lesson.driving) && Math.hypot(session.state.car.vx, session.state.car.vy) > 1) this.stats.racing(dt * 1000);
    // A phone's car stuck against something: how to back out.
    // (In the lesson, only on the rest of its practice lap: at turn 1 the lesson puts the car back itself.)
    if (this.mode === 'running' && !this.noteHold && (!lesson || lesson.practising) && this.shotAt < 0 && !this.autopilot) this.watchRescue(dt);
    else if (this.mode !== 'paused' && (this.rescue.showing.backOut || this.rescue.showing.restart)) {
      this.rescue.clear();
      this.showRescue();
    }
    // The lesson's caption, speed bar and road markings; otherwise the driving line, if it's on.
    if (lesson) lesson.display(session.state.car, now / 1000);
    else if (this.mode === 'running' || this.mode === 'watching') this.world.lesson?.update(trackPosition((watching ?? session).state.car, this.circuit.runtime), now / 1000);
    if (watching) this.watchProgress(watching);
    // The car on screen: yours, or the one being watched.
    const s = watching ?? session;
    const alpha = running ? Math.min(1, this.acc / SIM_DT) : 1;
    const renderTick = s.state.tick - 1 + alpha;
    if (watching && this.liveWatch) {
      this.hud.setWatchProgress(watching.state.car.s / this.circuit.track.length);
      this.hud.setLiveLine(this.liveLine());
    } else if (watching && !watching.finished) this.hud.setWatchProgress((watching.lapElapsedMs(renderTick) ?? 0) / (this.watchedLapMs() || 1));

    // Interpolated player pose.
    const c = s.state.car;
    const x = s.prevX + (c.x - s.prevX) * alpha;
    const y = s.prevY + (c.y - s.prevY) * alpha;
    let dh = c.heading - s.prevHeading;
    if (dh > Math.PI) dh -= Math.PI * 2;
    else if (dh < -Math.PI) dh += Math.PI * 2;
    const heading = s.prevHeading + dh * alpha;
    const sf = c.surfaces;
    const curbMask = (sf[0] === 1 ? 1 : 0) | (sf[1] === 1 ? 2 : 0) | (sf[2] === 1 ? 4 : 0) | (sf[3] === 1 ? 8 : 0);
    // Rough ground (grass, gravel) bounces and shakes; turf only rustles; painted tarmac runoff feels like track.
    const offWheels = sf.filter((v) => v === Surface.Grass || v === Surface.Gravel).length;
    const turfWheels = sf.filter((v) => v === Surface.Turf).length;
    // The road's height under the car (interpolated along the track like the pose) and its slope.
    const L = this.circuit.track.length;
    let dS = c.s - s.prevS;
    if (dS < -L / 2) dS += L;
    else if (dS > L / 2) dS -= L;
    const sNow = s.prevS + dS * alpha;
    const road = this.road(sNow, heading);
    const d = road.bank !== 0 ? this.lateral(sNow, x, y) : 0;
    const behind = this.road(sNow - CAMERA.distance, heading);
    const player: CarVisual = {
      x,
      z: -y,
      y: road.h + this.tilt(road.bank, d),
      pitch: road.pitch,
      roll: Math.atan(road.bank),
      yaw: heading,
      wheelAngle: c.wheelAngle,
      speed: c.vx,
      ax: c.ax,
      ay: c.ay,
      curbMask,
      offWheels,
      impact: c.barrierImpact,
    };
    const cam: CameraTarget = {
      x,
      z: -y,
      // The camera rides the slope behind the car (and looks at the road ahead), so hills tilt the view, not the framing.
      y: behind.h + this.tilt(behind.bank, d),
      lookY: this.road(sNow + CAMERA.lookAhead, heading).h + this.tilt(road.bank, d),
      yaw: heading,
      speed: Math.hypot(c.vx, c.vy),
      slip: c.vx > 2 ? Math.atan2(c.vy, c.vx) : 0,
      ax: c.ax,
      offTrack: offWheels > 0,
      impact: c.barrierImpact,
      roll: Math.atan(road.bank),
      // Paused, or a lesson's freeze: the picture holds still (no speed shake behind the pause poster).
      frozen: this.mode === 'paused' || !!lesson?.frozen,
    };
    let ghost: CarVisual | null = null;
    const go = this.ghostOut;
    // No ghost during the lesson: one car to think about.
    if (!lesson && (!this.filming || this.filmGhosts) && s.ghostPose(renderTick, go)) {
      const gr = this.road(go[8], go[2]);
      const gd = gr.bank !== 0 ? this.lateral(go[8], go[0], go[1]) : 0;
      ghost = { x: go[0], z: -go[1], y: gr.h + this.tilt(gr.bank, gd), pitch: gr.pitch, roll: Math.atan(gr.bank), yaw: go[2], wheelAngle: go[3], speed: go[4], ax: go[5], ay: 0, curbMask: go[6], offWheels: 0, impact: 0 };
    }

    // Crowd gets excited near the grandstand and at the line.
    const [gx, gz] = this.world.grandstandPos;
    const near = Math.max(0, 1 - Math.hypot(gx - x, gz + y) / 140);
    this.excitement = Math.max(near * 0.7, this.excitement - dt * 0.4);

    // Watching a podium: the laps not being followed run as ghosts, each with its driver's name over it.
    const pw = watching ? this.group : null;
    const others = pw ? pw.runners.map((sp, i) => ({ sp, i })).filter((r) => r.sp !== watching && !r.sp.done && !(this.ownGhostOff && pw.laps[r.i].own)) : [];
    // Watching live: the other drivers on track, as ghosts; racing, the friends driving now (the nearest few).
    // (A friend who's paused isn't left parked on your line.)
    const liveCars =
      watching && this.liveWatch
        ? this.liveWatch.others()
        : !watching && this.mode === 'running' && this.together && (this.friendsOn || this.raceWith)
          ? this.nearestFriends(this.together.ready().filter((c) => !c.paused && (this.friendsOn || c.key === this.raceWith?.key)))
          : [];
    const ghosts = pw ? others.map((r) => this.carVisual(r.sp, alpha)) : liveCars.map((c) => this.carVisual(c.sp, alpha));
    const ghostsGold = pw ? others.map((r) => pw.laps[r.i].gold === true) : liveCars.length ? liveCars.map((c) => c.gold) : undefined;
    this.world.render({ dt, player, camera: cam, ghost, ghosts, ghostsGold, orbit: this.mode === 'title', excitement: this.excitement });
    if (watching && this.group) this.groupTags(watching, others, ghosts, renderTick);
    else if (liveCars.length) {
      this.liveFlags(liveCars, ghosts);
      this.togetherFlags = true;
    } else if (this.togetherFlags) {
      this.hud.setGhostTags([]);
      this.togetherFlags = false;
    }
    if (liveCars.length && !watching && this.togetherCounted !== this.liveRun) {
      this.togetherCounted = this.liveRun;
      this.stats.count('liveTogether');
    }
    // HUD.
    const t = s.state.timing;
    this.hud.setTime(s.lapElapsedMs(renderTick), t.started && !t.valid);
    // Watching with your ghost out: your gap to their lap, said as yours ("you +0.31").
    if (watching && this.liveWatch) this.hud.setDelta(this.liveWatch.focusCar?.split ?? null);
    else if (watching) this.hud.setDelta(this.versusGap(watching, renderTick), 'you');
    else this.hud.setDelta(s.liveDelta(renderTick));
    this.hud.tick(dt);
    this.minimap?.draw({ x, y }, ghost ? { x: go[0], y: go[1] } : null);
    if (this.hud.fpsEl.style.display !== 'none') this.hud.fpsEl.textContent = `${this.fps.toFixed(0)} fps · ${this.renderer.info.render.calls} calls`;
    this.dev.tick(dt);

    // Sound.
    const p = s.physics;
    const slipF = Math.abs(c.slipFront) / p.peakSlipFront;
    const slipR = Math.abs(c.slipRear) / p.peakSlipRear;
    // Tyres squeal on anything hard: asphalt, curbs, painted runoff.
    const onAsphalt = sf.filter((v) => v <= 1 || v === Surface.Runoff).length / 4;
    this.audio.update({
      rpm: c.rpm,
      redline: p.redlineRpm,
      throttle: running ? c.throttle : 0,
      speed: Math.hypot(c.vx, c.vy),
      squeal: onAsphalt * Math.min(1, Math.max(0, Math.max(slipF, slipR) - 0.85) * 2.5 + c.lockup + c.wheelspin * 0.6),
      curb: curbMask !== 0,
      offroad: (offWheels + turfWheels * 0.35) / 4,
      gravel: sf.some((v) => v === 3),
      crowd: near,
    });
  }
}

/** Whether this browser can draw WebGL at all (the game needs it). */
function canDraw3d(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') ?? c.getContext('webgl'));
  } catch {
    return false;
  }
}

if (moveState === 'leaving') {
  // The game lives at grandprem.io now: on its way there (game/move.ts).
} else if (!canDraw3d()) {
  bootProblem('no3d');
  reportProblem('no3d', 'no WebGL', 'start');
} else {
  try {
    const game = new Game();
    game.start().catch((e: unknown) => game.die(e));
  } catch (e) {
    // Building the game failed (most often the 3D context): say so rather than leave the page blank.
    console.error(e);
    bootProblem(String(e).includes('WebGL') ? 'no3d' : 'crash');
    reportProblem(String(e).includes('WebGL') ? 'no3d' : 'crash', e, 'start');
  }
}
